import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useParams, Navigate, Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import Navbar from '../components/layout/Navbar';
import { useAuth } from '../context/AuthContext';
import {
  Users, BarChart2, Settings, CheckCircle, XCircle, Plus, Upload,
  Search, Filter, ArrowUpDown, Edit3, Trash2, UserPlus, Mail, Link2, Copy, X,
  ChevronDown, Shield, TrendingUp, Clock, AlertTriangle, Archive, Eye, Flag,
  Play, Pause, Calendar, ChevronRight, ChevronLeft, Download
} from 'lucide-react';
import PageHeader from '../components/layout/PageHeader';
import { PUBLIC_WEB_URL } from '../config/api';
import ReportDownload from '../components/ReportDownload';
import BulkAddMembers from '../components/BulkAddMembers';
import TeamMembersPicker from '../components/TeamMembersPicker';
import VerificationForm from '../components/VerificationForm';
import GuidedTour from '../components/tour/GuidedTour';
import MemberPills, { hasMemberPills } from '../components/membership/MemberPills';
import MembershipSettingsCard from '../components/membership/MembershipSettingsCard';
import MembershipReport from '../components/membership/MembershipReport';
import KitSwapSettingsCard from '../components/kitswap/KitSwapSettingsCard';
import ShopManager from '../components/shop/ShopManager';
import FixtureFinderSettingsCard from '../components/fixtureFinder/FixtureFinderSettingsCard';
import KitReportThread from '../components/kitswap/KitReportThread';
import { WhatsAppButton } from '../components/ShareMenu';
import { inviteShareText } from '../lib/share';
import { formatGBP } from '../lib/money';
import { OFFER_MAX, TERMS_MAX, discountLabel, offerMismatch } from '../lib/sponsorOffer';
import { trimUploadedLogo } from '../lib/logoTrim';
import './AdminPage.css';

// Note: 'sponsor' is NOT a member role — sponsors have separate accounts created via the Sponsors tab
const ROLES = ['admin', 'team_manager', 'coach', 'player', 'parent', 'supporter'];
const ROLE_LABELS = { admin: 'Admin', team_manager: 'Team Manager', coach: 'Coach', player: 'Player', parent: 'Parent / Guardian', supporter: 'Supporter' };
const ROLE_COLORS = { admin: 'badge-accent', team_manager: 'badge-gold', coach: 'badge-blue', player: 'badge-green', parent: 'badge-silver', supporter: 'badge-silver' };
// W-3 (Stuart, 10 Sept): club colour choice is LOCKED for now — the section is
// hidden, existing stored colours still render everywhere, nothing is wiped.
// Flip to false to bring the pickers back; the full remove-theming-entirely
// decision is an open roadmap question.
const CLUB_COLOURS_LOCKED = true;
const CLUB_COLOR_DEFAULTS = { primary_color: '#1E4A35', secondary_color: '#2C2B28', tertiary_color: '#F4EFE6', accent_color: '#C4943A' };
const CLUB_COLOR_FIELDS = [['primary_color', 'Primary'], ['secondary_color', 'Secondary'], ['tertiary_color', 'Tertiary'], ['accent_color', 'Accent']];
// How many colours a club has actually customised (trailing defaults don't count). Min 1.
const clubColoursUsed = (src) => {
  let n = 1;
  CLUB_COLOR_FIELDS.forEach(([key], i) => {
    if (i > 0 && src?.[key]) n = i + 1; // a colour counts only if it's actually stored (non-null)
  });
  return n;
};
const TEAM_ROLE_LABELS = { team_manager: 'Team manager', coach: 'Coach', player: 'Player' };

// Plain-English summary of what an invite grants. Reads the rich v14 payload
// when present; falls back to the legacy single role for older invites.
const describeInvitePayload = (inv) => {
  const p = inv && inv.payload;
  const rich = p && (p.clubAdmin || (Array.isArray(p.teamRoles) && p.teamRoles.length) || p.guardianIntent);
  if (inv && inv.kind === 'sponsor') return p?.sponsorName ? `Sponsor · ${p.sponsorName}` : 'Sponsor'; // UC-B3
  if (!rich) return ROLE_LABELS[inv.role] || inv.role;
  const parts = [];
  if (p.clubAdmin) parts.push('Club admin');
  (p.teamRoles || []).forEach(tr => {
    const rl = TEAM_ROLE_LABELS[tr.role] || tr.role;
    parts.push(tr.teamName ? `${rl} · ${tr.teamName}` : rl);
  });
  if (p.guardianIntent) parts.push('Parent / Guardian');
  return parts.join(', ');
};
const SORT_OPTIONS = [
  { value: 'default', label: 'Default' },
  { value: 'name_asc', label: 'Name A-Z' },
  { value: 'name_desc', label: 'Name Z-A' },
  { value: 'joined_newest', label: 'Newest first' },
  { value: 'joined_oldest', label: 'Oldest first' },
];

const AdminPage = () => {
  const { clubSlug } = useParams();
  const { user, getMembership, loading } = useAuth();
  const membership = getMembership(clubSlug);
  // Only club admins may grant the club-admin hat (authority-gated, mirrors backend).
  const isClubAdmin = !!(membership && (membership.clubAdmin || membership.role === 'admin')) || !!(user && user.isPlatformUser);
  // B-11 (27 Aug): team managers get in too — scoped to the Members tab. The
  // backend has allowed TM invites (players/coaches, own teams) since P3; this
  // is the door that was hidden. Detect both the headline role and a TM hat.
  const isTeamManager = !!membership && (
    membership.role === 'team_manager'
    || (Array.isArray(membership.teamRoles) && membership.teamRoles.some(tr => tr.role === 'team_manager'))
  );
  const qc = useQueryClient();
  const [tab, setTab] = useState('members'); // 17 Sept: People → Members is the landing screen
  // W-2: deep links (`/admin?tab=sponsors`) from the hub banner, the guided
  // tour and the sponsor-page explainers land on a specific tab. Read-only —
  // clicking tabs doesn't rewrite the URL.
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  useEffect(() => {
    const allowed = isClubAdmin
      ? ['teams', 'members', 'settings', 'sponsors', 'products', 'exports', 'report-membership', 'report-sponsorship', 'reports']
      : ['members', 'exports']; // team managers: only their tabs (mirrors `groups` below)
    if (tabParam && allowed.includes(tabParam)) setTab(tabParam);
  }, [tabParam, isClubAdmin]);
  // Team managers land on the only tab they have.
  useEffect(() => {
    if (!isClubAdmin && isTeamManager && tab === 'teams') setTab('members');
  }, [isClubAdmin, isTeamManager, tab]);
  const tabRefs = useRef({});

  // Report export filters (26 Aug). Attendance defaults to the whole record —
  // a club mid-season wants "everything so far", and narrowing is one tap.
  const [attFrom, setAttFrom] = useState('');
  const [attTo, setAttTo] = useState('');
  const [attTeam, setAttTeam] = useState('');
  const [attType, setAttType] = useState('');
  const [attEmails, setAttEmails] = useState(false);
  const [spPeriod, setSpPeriod] = useState('all');
  const [spFrom, setSpFrom] = useState('');   // 17 Sept: explicit window beats the preset period
  const [spTo, setSpTo] = useState('');
  const [spCustom, setSpCustom] = useState(false);   // B-48 / UC-8: From/To live in a fold

  // Members state
  const [memberSearch, setMemberSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [sortBy, setSortBy] = useState('default');
  const [memberScope, setMemberScope] = useState('club'); // 'club' | 'teams' (B-39 header)
  const [memberTotal, setMemberTotal] = useState(null);   // X-Total-Count: unfiltered, in scope (17 Sept)
  const [editingMember, setEditingMember] = useState(null);
  const [editRole, setEditRole] = useState('');
  const [removingMember, setRemovingMember] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showBulkAdd, setShowBulkAdd] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);   // 17 Sept: one Add button (Invite one · Add many)
  const [showInvites, setShowInvites] = useState(false);   // 17 Sept: pending invites collapsed by default
  const [editMemberForm, setEditMemberForm] = useState({});
  const [showArchivedMembers, setShowArchivedMembers] = useState(false);
  // 17 Sept: one filter for the three tiers — '' | 'community' | 'club' | 'staff'
  // (client-side; the list is already loaded). Replaces the "club members only"
  // toggle and the whole bulk-select mode (dropped — the pill per row is the job;
  // the server's POST /membership/members/bulk stays for the GMS import later).
  const [statusFilter, setStatusFilter] = useState('');

  // Invite state. The create flow uses the additive multi-role wizard:
  // a single team involvement (one role per team), plus optional guardian
  // and club-admin hats. inviteRole is retained only for the legacy
  // inline edit-invite form below.
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('player');
  const [inviteTeamRole, setInviteTeamRole] = useState('none'); // none | player | coach | team_manager
  const [inviteTeamId, setInviteTeamId] = useState('');
  const [inviteGuardian, setInviteGuardian] = useState(false);
  const [inviteClubAdmin, setInviteClubAdmin] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [copiedInviteId, setCopiedInviteId] = useState(null); // tracks which row's link was just copied
  const [editingInviteId, setEditingInviteId] = useState(null);
  const [editInviteForm, setEditInviteForm] = useState({ email: '', role: '' });
  const [resentInviteId, setResentInviteId] = useState(null);

  // Sponsor state — unified "Set up sponsorship" wizard (create + edit)
  const EMPTY_SPONSOR_FORM = {
    business_name: '', category: '', website: '', contact_name: '', contact_email: '', logo_url: '',
    offer_title: '', offer_value: '', offer_description: '',
    discount_type: 'percentage', discount_rate: '', min_spend: '', max_discount_value: '',
    tier: 'standard', featured: false, sponsorship_cost: '', sponsorship_start: '', sponsorship_end: '',
  };
  // wizard: { open, mode:'create'|'edit', step:1..4, sponsorId, goLive:'start_now'|'schedule'|'draft' }
  const [sponsorWizard, setSponsorWizard] = useState({ open: false, mode: 'create', step: 1, sponsorId: null, goLive: 'start_now' });
  const [sponsorForm, setSponsorForm] = useState(EMPTY_SPONSOR_FORM);
  // Stuart, 29 Oct: logos trimmed of their empty edge (see handleSponsorLogoChange).
  const [logoTrimmed, setLogoTrimmed] = useState(false);
  // An existing uploaded logo can be trimmed too (one tap, on step 1).
  const [existingLogoTrim, setExistingLogoTrim] = useState(null);
  useEffect(() => {
    setExistingLogoTrim(null);
    if (!sponsorWizard.open || sponsorWizard.mode !== 'edit' || logoTrimmed) return undefined;
    const src = sponsorForm.logo_url;
    if (!src || !src.startsWith('data:')) return undefined;
    let alive = true;
    trimUploadedLogo(src).then(r => { if (alive && r.trimmed) setExistingLogoTrim(r.src); });
    return () => { alive = false; };
  }, [sponsorWizard.open, sponsorWizard.mode, sponsorForm.logo_url, logoTrimmed]);
  const [creatingAccountFor, setCreatingAccountFor] = useState(null); // sponsor object
  const [sponsorAccount, setSponsorAccount] = useState({ email: '', firstName: '', lastName: '' });
  const [sponsorInvite, setSponsorInvite] = useState(null); // UC-B3: { inviteUrl, email } once sent
  const [sponsorLinkCopied, setSponsorLinkCopied] = useState(false);
  const [showArchivedSponsors, setShowArchivedSponsors] = useState(false);

  // Teams state
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [newTeam, setNewTeam] = useState({ name: '', age_group: '', sport: '', description: '' });
  const [editingTeam, setEditingTeam] = useState(null);
  const [addingToTeam, setAddingToTeam] = useState(null); // B-1 bulk picker
  const [editTeamForm, setEditTeamForm] = useState({});
  const [showArchivedTeams, setShowArchivedTeams] = useState(false);

  // Settings state
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoSaving, setLogoSaving] = useState(false);

  // Home ground state. Mapbox-powered autocomplete: user types any of name,
  // postcode, address — suggestions appear, pick one to pin coords.
  // `name` is the friendly display label shown on event cards.
  // `searchQuery` is the autocomplete input value (separate from name so user
  // can search "Nailsea Rugby" but call it "West End Park").
  const [groundForm, setGroundForm] = useState({
    name: '',
    resolvedAddress: '',
    lat: null,
    lng: null,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [groundSaving, setGroundSaving] = useState(false);

  // Club identity (nickname / founded / phone / website / colours) — shown in the hero.
  const [identityForm, setIdentityForm] = useState({ nickname: '', founded_year: '', phone: '', website: '', sport: '', rsvp_deadline_days: 3, primary_color: '#1E4A35', secondary_color: '#2C2B28', tertiary_color: '#F4EFE6', accent_color: '#C4943A' });
  const [identityColorCount, setIdentityColorCount] = useState(1); // colours shown in settings (start with 1; "+" adds up to 4)
  const [identitySaving, setIdentitySaving] = useState(false);
  const [identitySaved, setIdentitySaved] = useState(false);

  // Debounce timer for the Mapbox autocomplete (used by handleSearchChange
  // further down). Declared here with the other hooks — a useRef must run on
  // every render, never below the early returns.
  const searchDebounceRef = React.useRef();

  // Queries
  const { data: club } = useQuery({ queryKey: ['club', clubSlug], queryFn: () => axios.get(`/clubs/${clubSlug}`).then(r => r.data) });

  // Sync home-ground form when club data loads.
  // IMPORTANT: must be declared AFTER `club` — referencing club in the deps
  // array before useQuery returns it throws a temporal-dead-zone error.
  React.useEffect(() => {
    if (club) {
      setGroundForm({
        name: club.home_ground_name || '',
        resolvedAddress: club.home_ground_address || '',
        lat: club.home_ground_lat || null,
        lng: club.home_ground_lng || null,
      });
    }
  }, [club?.id, club?.home_ground_name, club?.home_ground_address, club?.home_ground_lat, club?.home_ground_lng]);

  // Sync identity form when club data loads (same pattern as ground form).
  React.useEffect(() => {
    if (club) {
      setIdentityForm({
        nickname: club.nickname || '',
        founded_year: club.founded_year || '',
        phone: club.phone || '',
        website: club.website || '',
        sport: club.sport || '',
        rsvp_deadline_days: club.rsvp_deadline_days ?? 3,
        primary_color: club.primary_color || '#1E4A35',
        secondary_color: club.secondary_color || '#2C2B28',
        tertiary_color: club.tertiary_color || '#F4EFE6',
        accent_color: club.accent_color || '#C4943A',
      });
      setIdentityColorCount(clubColoursUsed(club));
    }
  }, [club?.id, club?.nickname, club?.founded_year, club?.phone, club?.website, club?.sport, club?.rsvp_deadline_days, club?.primary_color, club?.secondary_color, club?.tertiary_color, club?.accent_color]);
  const { data: members = [], isLoading: membersLoading } = useQuery({
    queryKey: ['members', clubSlug, memberSearch, roleFilter, teamFilter, sortBy],
    queryFn: () => {
      const params = new URLSearchParams();
      if (memberSearch) params.set('search', memberSearch);
      if (roleFilter) params.set('role', roleFilter);
      if (teamFilter) params.set('team_id', teamFilter);
      if (sortBy !== 'default') params.set('sort', sortBy);
      // B-39: the server scopes this list to the caller's teams below club
      // admin and says so in a header (headers, not envelopes — frozen API).
      return axios.get(`/clubs/${clubSlug}/members?${params}`).then(r => {
        setMemberScope(r.headers?.['x-member-scope'] || 'club');
        const total = Number(r.headers?.['x-total-count']);
        if (Number.isFinite(total)) setMemberTotal(total);
        return r.data;
      });
    },
    enabled: !!membership
  });
  const { data: teams = [] } = useQuery({ queryKey: ['teams', clubSlug], queryFn: () => axios.get(`/clubs/${clubSlug}/teams`).then(r => r.data) });
  // Teams the current user may invite people INTO: admins all of them, team
  // managers only the teams they manage (mirrors the backend authority gate).
  const invitableTeams = isClubAdmin
    ? teams
    : teams.filter(t => (membership?.teamRoles || []).some(tr => tr.teamId === t.id && tr.role === 'team_manager'));
  const { data: allTeams = [] } = useQuery({ queryKey: ['allTeams', clubSlug], queryFn: () => axios.get(`/clubs/${clubSlug}/teams?includeInactive=true`).then(r => r.data), enabled: !!membership });
  // B-24 (26 Aug): this sent NO period, which the backend reads as all-time —
  // fine, but it was accidental rather than chosen, and it sat next to a
  // sponsor-facing page whose identical figures are 30-day by default. Now
  // all-time is explicit. (Follow-up B-36: give this tab the same
  // 7d/30d/90d/all pill the sponsor attribution page has, so a club admin can
  // scope it — until then the numbers here are lifetime totals by design.)
  // v33: commercial endpoints 403 until the club is verified — don't fire
  // them for an unverified club (the Sponsors tab shows an explainer instead).
  const clubVerified = !!club?.verified_at;
  // B-51: club admins only — team managers reach this page too, and both
  // endpoints 403 for them (the tour check flagged the wasted calls).
  const { data: report = {} } = useQuery({ queryKey: ['report', clubSlug], queryFn: () => axios.get(`/clubs/${clubSlug}/sponsors/report/redemptions?period=all`).then(r => r.data), enabled: !!membership && isClubAdmin && clubVerified });
  const { data: sponsors = [] } = useQuery({ queryKey: ['sponsors', clubSlug, showArchivedSponsors], queryFn: () => {
    const params = new URLSearchParams();
    if (showArchivedSponsors) params.set('includeArchived', 'true');
    return axios.get(`/clubs/${clubSlug}/sponsors/manage?${params}`).then(r => r.data);
  }, enabled: !!membership && isClubAdmin && clubVerified });
  // B-49: the Shop pill and the Shop screen's heading count the same thing —
  // every item in the manager, shown or hidden (same query as ShopManager).
  const { data: shopAdmin } = useQuery({
    queryKey: ['shop-admin', clubSlug, 'items'],
    queryFn: () => axios.get(`/clubs/${clubSlug}/shop/admin`).then(r => r.data),
    enabled: !!isClubAdmin,
  });
  const shopItemCount = shopAdmin?.items?.length || 0;
  const newShopEnquiries = shopAdmin?.enquiry_counts?.new || 0; // QA r2: New enquiries light the Club dot too
  const { data: invites = [] } = useQuery({ queryKey: ['invites', clubSlug], queryFn: () => axios.get(`/clubs/${clubSlug}/members/invites?includeExpired=30`).then(r => r.data), enabled: !!membership }); // B-38 follow-up: + invites expired in the last 30 days

  // UGC message reports (Play UGC policy) — flagged chat messages
  // WT-13: wait for sign-in (it fired on a cold load and took a 401), and only
  // for the roles the server lets in (admin / team manager).
  const { data: openReports = [] } = useQuery({
    queryKey: ['message-reports', clubSlug],
    queryFn: () => axios.get(`/clubs/${clubSlug}/message-reports?status=open`).then(r => r.data),
    enabled: !!membership && (isClubAdmin || isTeamManager),
  });
  // B-58 slice 3: waiting join requests — the same count (and query key) as
  // the badge on the navbar's Admin door, so the People dot and the badge agree.
  const { data: joinWaiting = 0 } = useQuery({
    queryKey: ['join-requests-count', clubSlug],
    queryFn: () => axios.get(`/clubs/${clubSlug}/join-requests/count`).then(r => Number(r.data?.waiting) || 0),
    enabled: !!membership && isClubAdmin,
    staleTime: 60000,
    retry: false,
  });
  const resolveReportMutation = useMutation({
    mutationFn: ({ reportId, action }) => axios.patch(`/clubs/${clubSlug}/message-reports/${reportId}`, { action }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['message-reports', clubSlug] }),
    onError: (err) => alert(err.response?.data?.error || 'Failed to resolve report'),
  });

  // Mutations
  const approveMutation = useMutation({
    mutationFn: (memberId) => axios.patch(`/clubs/${clubSlug}/members/${memberId}/approve`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['members', clubSlug] });
      qc.invalidateQueries({ queryKey: ['join-requests-count', clubSlug] }); // B-58: the Admin door's badge
    }
  });

  // B-58: decline a waiting join request (neutral notice; 30-day wait).
  const declineMutation = useMutation({
    mutationFn: (memberId) => axios.post(`/clubs/${clubSlug}/members/${memberId}/decline`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['members', clubSlug] });
      qc.invalidateQueries({ queryKey: ['join-requests-count', clubSlug] });
    },
    onError: (err) => alert(err.response?.data?.error || 'Failed to decline the request')
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ memberId, role }) => axios.patch(`/clubs/${clubSlug}/members/${memberId}/role`, { role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['members', clubSlug] }),
    onError: (err) => alert(err.response?.data?.error || 'Failed to update role')
  });

  const removeMemberMutation = useMutation({
    mutationFn: (memberId) => axios.delete(`/clubs/${clubSlug}/members/${memberId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['members', clubSlug] });
      qc.invalidateQueries({ queryKey: ['join-requests-count', clubSlug] }); // B-58: may have been a waiting row
      setRemovingMember(null);
    },
    onError: (err) => alert(err.response?.data?.error || 'Failed to remove member')
  });

  const updateMemberDetailsMutation = useMutation({
    mutationFn: ({ memberId, data }) => axios.patch(`/clubs/${clubSlug}/members/${memberId}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['members', clubSlug] }),
    onError: (err) => alert(err.response?.data?.error || 'Failed to update member')
  });

  const updateMemberStatusMutation = useMutation({
    mutationFn: ({ memberId, is_active }) => axios.patch(`/clubs/${clubSlug}/members/${memberId}/status`, { is_active }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['members', clubSlug] });
    },
    onError: (err) => alert(err.response?.data?.error || 'Failed to update status')
  });

  const inviteMutation = useMutation({
    mutationFn: (data) => axios.post(`/clubs/${clubSlug}/members/invite`, data),
    onSuccess: (res) => {
      setGeneratedLink(res.data.inviteUrl);
      qc.invalidateQueries({ queryKey: ['invites', clubSlug] });
    },
    onError: (err) => setInviteError(err.response?.data?.error || 'Failed to create invite')
  });

  const revokeInviteMutation = useMutation({
    mutationFn: (inviteId) => axios.delete(`/clubs/${clubSlug}/members/invites/${inviteId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invites', clubSlug] })
  });

  const editInviteMutation = useMutation({
    mutationFn: ({ inviteId, data }) => axios.patch(`/clubs/${clubSlug}/members/invites/${inviteId}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invites', clubSlug] });
      setEditingInviteId(null);
    },
    onError: (err) => alert(err.response?.data?.error || 'Failed to update invite')
  });

  const resendInviteMutation = useMutation({
    mutationFn: (inviteId) => axios.post(`/clubs/${clubSlug}/members/invites/${inviteId}/resend`),
    onSuccess: (_, inviteId) => {
      // B-38: a resend can renew an expired/undated invite's expiry — refetch.
      qc.invalidateQueries({ queryKey: ['invites', clubSlug] });
      setResentInviteId(inviteId);
      setTimeout(() => setResentInviteId(null), 3000);
    },
    onError: (err) => alert(err.response?.data?.error || 'Failed to resend invite')
  });

  // Create a sponsor (draft), then apply the chosen go-live in one flow.
  const createSponsorMutation = useMutation({
    mutationFn: async ({ form, goLive }) => {
      const payload = {
        businessName: form.business_name, category: form.category, website: form.website,
        contactName: form.contact_name, contactEmail: form.contact_email, logoUrl: form.logo_url,
        offerTitle: form.offer_title, offerValue: form.offer_value, offerDescription: form.offer_description,
        discountType: form.discount_type, discountRate: form.discount_rate,
        minSpend: form.min_spend, maxDiscountValue: form.max_discount_value,
        tier: form.tier, featured: form.featured,
        sponsorshipCost: form.sponsorship_cost, sponsorshipStart: form.sponsorship_start, sponsorshipEnd: form.sponsorship_end,
      };
      const res = await axios.post(`/clubs/${clubSlug}/sponsors`, payload);
      const id = res.data.id;
      if (goLive === 'start_now') {
        await axios.patch(`/clubs/${clubSlug}/sponsors/${id}/lifecycle`, { action: 'start', sponsorshipEnd: form.sponsorship_end });
      } else if (goLive === 'schedule') {
        await axios.patch(`/clubs/${clubSlug}/sponsors/${id}/lifecycle`, { action: 'schedule', sponsorshipStart: form.sponsorship_start, sponsorshipEnd: form.sponsorship_end });
      }
      return res;
    },
    onSuccess: () => {
      closeSponsorWizard();
      qc.invalidateQueries({ queryKey: ['sponsors', clubSlug] });
      qc.invalidateQueries({ queryKey: ['report', clubSlug] });
    },
    onError: (err) => alert(err.response?.data?.error || 'Failed to save sponsorship')
  });

  // UC-B3 (12 Oct): admins no longer type a sponsor's password. The sponsor
  // gets an invite link and sets their own (or signs in, if they already have
  // an account). The link is shown too, so the admin can send it themselves.
  const inviteSponsorMutation = useMutation({
    mutationFn: ({ sponsorId, data }) => axios.post(`/clubs/${clubSlug}/sponsors/${sponsorId}/invite`, data),
    onSuccess: (res) => {
      setSponsorInvite({ inviteUrl: res.data.inviteUrl, email: res.data.email });
      qc.invalidateQueries({ queryKey: ['invites', clubSlug] });
    },
  });
  const closeSponsorInvite = () => {
    setCreatingAccountFor(null);
    setSponsorInvite(null);
    setSponsorLinkCopied(false);
    inviteSponsorMutation.reset();
  };

  // Edit an existing sponsor's details (blocked server-side while live).
  const updateSponsorMutation = useMutation({
    mutationFn: ({ sponsorId, data }) => axios.patch(`/clubs/${clubSlug}/sponsors/${sponsorId}`, data),
    onSuccess: () => {
      closeSponsorWizard();
      qc.invalidateQueries({ queryKey: ['sponsors', clubSlug] });
      qc.invalidateQueries({ queryKey: ['report', clubSlug] });
    },
    onError: (err) => alert(err.response?.data?.error || 'Failed to update sponsor')
  });

  // Lifecycle actions from the sponsor row: start / stop / archive / reactivate.
  const lifecycleSponsorMutation = useMutation({
    mutationFn: ({ sponsorId, action }) => axios.patch(`/clubs/${clubSlug}/sponsors/${sponsorId}/lifecycle`, { action }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sponsors', clubSlug] });
      qc.invalidateQueries({ queryKey: ['report', clubSlug] });
    },
    onError: (err) => alert(err.response?.data?.error || 'Failed to update sponsorship status')
  });

  const createTeamMutation = useMutation({
    mutationFn: ({ name, age_group, sport, description }) => axios.post(`/clubs/${clubSlug}/teams`, {
      name, ageGroup: age_group, sport, description
    }),
    onSuccess: () => {
      setShowTeamForm(false);
      setNewTeam({ name: '', age_group: '', sport: '', description: '' });
      qc.invalidateQueries({ queryKey: ['teams', clubSlug] });
      qc.invalidateQueries({ queryKey: ['allTeams', clubSlug] });
    },
    onError: (err) => alert(err.response?.data?.error || 'Failed to create team')
  });

  const updateTeamMutation = useMutation({
    mutationFn: ({ teamId, data }) => axios.patch(`/clubs/${clubSlug}/teams/${teamId}`, {
      name: data.name,
      ageGroup: data.age_group,
      sport: data.sport,
      description: data.description,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['teams', clubSlug] });
      qc.invalidateQueries({ queryKey: ['allTeams', clubSlug] });
      setEditingTeam(null);
    },
    onError: (err) => alert(err.response?.data?.error || 'Failed to update team')
  });

  const toggleTeamStatusMutation = useMutation({
    mutationFn: ({ teamId, isActive }) => axios.patch(`/clubs/${clubSlug}/teams/${teamId}`, { isActive }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['teams', clubSlug] });
      qc.invalidateQueries({ queryKey: ['allTeams', clubSlug] });
    },
    onError: (err) => alert(err.response?.data?.error || 'Failed to update team status')
  });

  const accentColor = club?.accent_color || '#C4943A';

  if (loading) return <div className="page-loading"><div className="spinner" style={{ width: 40, height: 40 }} /></div>;
  // B-11: admins get everything; team managers get their scoped view.
  if (!user || !membership || (!isClubAdmin && !isTeamManager)) return <Navigate to={`/${clubSlug}`} replace />;

  const pending = members.filter(m => !m.is_approved);
  const approved = members.filter(m => m.is_approved);

  // v35 membership cards — per-club switch (Club Settings → Membership cards).
  // Until it's on, no pill, no Select, no filter, no report exists here.
  const cardsOn = !!club?.membership_cards_enabled;
  const reportBreakdown = report?.breakdown || [];
  // B-24: the server now filters to genuinely-pending invites (accepted and
  // expired excluded) before applying its cap, so this filter is a belt-and-
  // braces no-op rather than the load-bearing one it used to be. It used to
  // run on an already-truncated list of 50 mixed rows, which meant accepted
  // invites ate the window and real pending ones vanished from the screen.
  // B-38: a NULL expires_at means no expiry (the server lists it as pending);
  // new Date(null) is 1970, which used to hide those invites here.
  const inviteIsLive = (i) => !i.expires_at || new Date(i.expires_at) > new Date();
  const pendingInvites = invites.filter(i => !i.accepted_at && inviteIsLive(i));
  // B-38 follow-up: recently expired (≤30 days, server-side) invites, listed
  // after the live ones with "Expired {date}" and Resend (fresh 7 days).
  const expiredInvites = invites.filter(i => !i.accepted_at && !inviteIsLive(i));
  const inviteRows = [...pendingInvites, ...expiredInvites];
  const matchesStatus = (m) => {
    if (statusFilter === 'club') return !!m.is_member;
    if (statusFilter === 'staff') return !!m.can_scan;
    if (statusFilter === 'community') return !m.is_member && !m.can_scan; // in the app, not (yet) paid or staff
    return true;
  };
  const visibleMembers = approved.filter(m => (showArchivedMembers || m.is_active !== false) && matchesStatus(m));
  const activeFilterCount = [roleFilter, teamFilter, sortBy !== 'default', statusFilter].filter(Boolean).length;
  // Header count = everyone in scope; the line under the tools says what a
  // search / filter narrowed it to (search, role and team are server-side,
  // status and archived are client-side — the header total ignores them all).
  const membersInScope = memberTotal ?? approved.length;
  const narrowing = [
    memberSearch && `“${memberSearch}”`,
    roleFilter && (ROLE_LABELS[roleFilter] || roleFilter),
    teamFilter && (teams.find(t => t.id === teamFilter)?.name || 'team'),
    statusFilter === 'club' && 'club members',
    statusFilter === 'staff' && 'club staff',
    statusFilter === 'community' && 'community members',
    showArchivedMembers && 'including archived',
  ].filter(Boolean);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleLogoSave = async () => {
    if (!logoPreview) return;
    setLogoSaving(true);
    try {
      await axios.patch(`/clubs/${clubSlug}/settings`, { logo_url: logoPreview });
      qc.invalidateQueries({ queryKey: ['club', clubSlug] });
      setLogoPreview(null);
      alert('Logo updated!');
    } catch (err) {
      alert('Failed to save logo');
    } finally { setLogoSaving(false); }
  };

  // ─── Mapbox autocomplete search ────────────────────────────────────
  // User types a name / postcode / address — suggestions stream in after
  // a 300ms debounce. Picking a suggestion pins the coords directly.
  // Backend chooses Mapbox (if MAPBOX_ACCESS_TOKEN is set) or Nominatim.
  // (searchDebounceRef is declared up top with the other hooks — it must
  // never sit below the early returns or the hook count varies between renders.)
  const handleSearchChange = (val) => {
    setSearchQuery(val);
    setSearchError('');
    clearTimeout(searchDebounceRef.current);
    if (val.trim().length < 3) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    searchDebounceRef.current = setTimeout(async () => {
      try {
        // W-4: venue mode — sports-club-biased, drops fuzzy POI mismatches (the Kingston KFC).
        const r = await axios.get('/geocode', { params: { q: val.trim(), limit: 6, mode: 'venue' } });
        const results = r.data || [];
        setSearchResults(results);
        setSearchOpen(true);
        if (!results.length) setSearchError("Nothing matched — try the club name, postcode, or street address.");
      } catch (err) {
        setSearchError('Search failed — check your connection and try again.');
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  };

  const handlePickSearchResult = (c) => {
    setGroundForm(p => ({
      ...p,
      resolvedAddress: c.displayName,
      lat: c.lat,
      lng: c.lng,
      // v45: the geocoder's structured locality of THIS pick → clubs.town
      // (Fixture Finder). Only ever from a pick, never typed.
      locality: c.locality || null,
      picked: true,
      // If user hasn't typed a Ground Name, suggest the POI's name as one.
      name: p.name || c.name || c.displayName?.split(',')[0] || '',
    }));
    setSearchQuery(c.displayName || '');
    setSearchResults([]);
    setSearchOpen(false);
  };

  const handleClearGroundCoords = () => {
    setGroundForm(p => ({ ...p, resolvedAddress: '', lat: null, lng: null, locality: null, picked: false }));
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleSaveGround = async () => {
    setGroundSaving(true);
    try {
      await axios.patch(`/clubs/${clubSlug}/settings`, {
        home_ground_name: groundForm.name || null,
        home_ground_address: groundForm.resolvedAddress || null,
        home_ground_lat: groundForm.lat || null,
        home_ground_lng: groundForm.lng || null,
        ...(groundForm.picked && groundForm.lat ? { home_ground_locality: groundForm.locality || null } : {}),
      });
      qc.invalidateQueries({ queryKey: ['club', clubSlug] });
      // Backend cascades the new coords to future home fixtures, so refresh
      // the events cache too — otherwise ClubHub keeps serving stale rows.
      qc.invalidateQueries({ queryKey: ['events', clubSlug] });
      // WT-6: and Find a game, so it doesn't keep "can't place your ground" for a minute.
      qc.invalidateQueries({ queryKey: ['fixture-finder', clubSlug] });
      alert('Home ground saved! Home fixtures will now show a map and weather forecast.');
    } catch (err) {
      alert('Failed to save home ground');
    } finally { setGroundSaving(false); }
  };

  const handleGenerateInvite = () => {
    setInviteError('');
    setGeneratedLink('');

    if (inviteTeamRole !== 'none' && !inviteTeamId) {
      setInviteError('Pick which team they’ll be in.');
      return;
    }

    const teamRoles = [];
    if (inviteTeamRole !== 'none' && inviteTeamId) {
      const t = teams.find(x => x.id === inviteTeamId);
      teamRoles.push({ teamId: inviteTeamId, teamName: t ? t.name : null, role: inviteTeamRole });
    }
    const clubAdmin = isClubAdmin && inviteClubAdmin;
    const guardianIntent = inviteGuardian;

    // Nothing chosen → a plain supporter. Send via the legacy single-role
    // body, which the backend maps to club_members.role = 'supporter'.
    if (teamRoles.length === 0 && !clubAdmin && !guardianIntent) {
      inviteMutation.mutate({ email: inviteEmail || null, role: 'supporter' });
      return;
    }

    inviteMutation.mutate({
      email: inviteEmail || null,
      payload: { clubAdmin, guardianIntent, teamRoles },
    });
  };

  const resetInviteWizard = () => {
    setShowInviteModal(true);
    setGeneratedLink('');
    setInviteEmail('');
    setInviteError('');
    setInviteTeamRole('none');
    setInviteTeamId('');
    setInviteGuardian(false);
    setInviteClubAdmin(false);
  };

  // Live plain-English summary of the wizard selection.
  const inviteSummary = (() => {
    const parts = [];
    if (isClubAdmin && inviteClubAdmin) parts.push('Club admin');
    if (inviteTeamRole !== 'none') {
      const t = teams.find(x => x.id === inviteTeamId);
      const rl = TEAM_ROLE_LABELS[inviteTeamRole] || inviteTeamRole;
      parts.push(t ? `${rl} · ${t.name}` : `${rl} (pick a team)`);
    }
    if (inviteGuardian) parts.push('Parent / Guardian');
    return parts.length ? parts.join(', ') : 'Supporter — follows the club, no team';
  })();

  const handleCopyLink = () => {
    navigator.clipboard.writeText(generatedLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const closeSponsorWizard = () => {
    setSponsorWizard({ open: false, mode: 'create', step: 1, sponsorId: null, goLive: 'start_now' });
    setSponsorForm(EMPTY_SPONSOR_FORM);
    setLogoTrimmed(false);
  };

  const openCreateSponsorWizard = () => {
    setSponsorForm(EMPTY_SPONSOR_FORM);
    setSponsorWizard({ open: true, mode: 'create', step: 1, sponsorId: null, goLive: 'start_now' });
  };

  const openEditSponsorWizard = (sponsor) => {
    setSponsorForm({
      business_name: sponsor.business_name || '', category: sponsor.category || '',
      website: sponsor.website || '', contact_name: sponsor.contact_name || '',
      contact_email: sponsor.contact_email || '', logo_url: sponsor.logo_url || '',
      offer_title: sponsor.offer_title || '',
      offer_value: sponsor.offer_value || '', offer_description: sponsor.offer_description || '',
      discount_type: sponsor.discount_type || 'percentage', discount_rate: sponsor.discount_rate ?? '',
      min_spend: sponsor.min_spend ?? '', max_discount_value: sponsor.max_discount_value ?? '',
      tier: sponsor.tier || 'standard', featured: sponsor.featured || false,
      sponsorship_cost: sponsor.sponsorship_cost ?? '',
      sponsorship_start: (sponsor.sponsorship_start || '').slice(0, 10),
      sponsorship_end: (sponsor.sponsorship_end || '').slice(0, 10),
    });
    setSponsorWizard({ open: true, mode: 'edit', step: 1, sponsorId: sponsor.id, goLive: 'draft' });
  };

  const setSF = (patch) => setSponsorForm(p => ({ ...p, ...patch }));

  // Stuart, 29 Oct: logos more prominent — the empty margin round a logo is
  // cropped at upload (and the file capped at 800px), so it fills its box.
  const handleSponsorLogoChange = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async ev => {
      const { src, trimmed } = await trimUploadedLogo(ev.target.result);
      setSF({ logo_url: src });
      setLogoTrimmed(trimmed);
    };
    reader.readAsDataURL(file);
  };

  const submitSponsorWizard = () => {
    // The badge members see ("15% off") is made from the till discount, so
    // the two can't disagree. No till discount → an existing badge is left
    // as it was (edit) / none (create).
    const badge = discountLabel(sponsorForm.discount_type, sponsorForm.discount_rate);
    if (sponsorWizard.mode === 'edit') {
      const { featured, business_name, category, website, contact_name, contact_email, logo_url,
        offer_title, offer_description, discount_type, discount_rate,
        min_spend, max_discount_value, tier, sponsorship_cost, sponsorship_start, sponsorship_end } = sponsorForm;
      updateSponsorMutation.mutate({
        sponsorId: sponsorWizard.sponsorId,
        data: {
          business_name, category, website, contact_name, contact_email, logo_url,
          offer_title: offer_title.trim(), offer_description: offer_description.trim(), discount_type,
          ...(badge ? { offer_value: badge } : {}),
          discount_rate: discount_rate === '' ? null : discount_rate,
          min_spend: min_spend === '' ? null : min_spend,
          max_discount_value: max_discount_value === '' ? null : max_discount_value,
          tier, featured,
          sponsorship_cost: sponsorship_cost === '' ? null : sponsorship_cost,
          sponsorship_start: sponsorship_start || null,
          sponsorship_end: sponsorship_end || null,
        },
      });
    } else {
      createSponsorMutation.mutate({
        form: { ...sponsorForm, offer_title: sponsorForm.offer_title.trim(), offer_description: sponsorForm.offer_description.trim(), offer_value: badge },
        goLive: sponsorWizard.goLive,
      });
    }
  };

  const handleSponsorLifecycle = (sponsor, action) => {
    if (action === 'archive' && !window.confirm(`Archive ${sponsor.business_name}? Members will no longer see this offer. You can reactivate it later.`)) return;
    lifecycleSponsorMutation.mutate({ sponsorId: sponsor.id, action });
  };

  // Open the sponsor login invite pre-filled from the sponsor's contact details.
  const openCreateSponsorAccount = (sponsor) => {
    const parts = (sponsor.contact_name || '').trim().split(/\s+/).filter(Boolean);
    setCreatingAccountFor(sponsor);
    setSponsorAccount({
      email: sponsor.contact_email || '',
      firstName: parts[0] || '',
      lastName: parts.slice(1).join(' ') || '',
    });
    setSponsorInvite(null);
    setSponsorLinkCopied(false);
    inviteSponsorMutation.reset();
  };

  // Edit is available on any non-archived sponsor. A live offer is locked so
  // members see stable terms — clicking Edit offers to Stop it first, then opens
  // the wizard (start it again afterwards).
  const handleEditClick = (sponsor) => {
    const st = sponsor.status || (sponsor.is_active ? 'live' : 'archived');
    if (st === 'live') {
      if (window.confirm(`"${sponsor.business_name}" is live, so members currently see fixed offer terms. Stop it now so you can edit? You can start it again afterwards.`)) {
        lifecycleSponsorMutation.mutate({ sponsorId: sponsor.id, action: 'stop' }, {
          onSuccess: () => openEditSponsorWizard(sponsor),
        });
      }
      return;
    }
    openEditSponsorWizard(sponsor);
  };

  const handleEditTeam = (team) => {
    setEditingTeam(team);
    setEditTeamForm({
      name: team.name || '',
      age_group: team.age_group || '',
      sport: team.sport || '',
      description: team.description || '',
    });
  };

  const handleSaveEditTeam = () => {
    updateTeamMutation.mutate({ teamId: editingTeam.id, data: editTeamForm });
  };

  const handleToggleTeamStatus = (team) => {
    toggleTeamStatusMutation.mutate({ teamId: team.id, isActive: !team.is_active });
  };

  // 17 Sept (Stuart): ONE Edit button per row → one modal for role + team +
  // jersey number, with "Remove from the club" tucked at the bottom. Saves only
  // what changed (role and details are separate endpoints).
  const openEditMember = (member) => {
    setEditingMember(member);
    setEditRole(member.role);
    setEditMemberForm({
      jersey_number: member.jersey_number || '',
      team_id: member.teams?.[0]?.team_id || ''
    });
  };
  const editRoleChanged = !!editingMember && editRole !== editingMember.role;
  const editDetailsChanged = !!editingMember && (
    String(editMemberForm.jersey_number ?? '') !== String(editingMember.jersey_number || '') ||
    String(editMemberForm.team_id ?? '') !== String(editingMember.teams?.[0]?.team_id || '')
  );
  const editSaving = updateRoleMutation.isPending || updateMemberDetailsMutation.isPending;
  const handleSaveEditMember = async () => {
    if (!editingMember) return;
    try {
      if (editRoleChanged) await updateRoleMutation.mutateAsync({ memberId: editingMember.id, role: editRole });
      if (editDetailsChanged) {
        await updateMemberDetailsMutation.mutateAsync({
          memberId: editingMember.id,
          data: { jersey_number: editMemberForm.jersey_number, team_id: editMemberForm.team_id || null },
        });
      }
      setEditingMember(null);
    } catch { /* the mutation's onError already told the user */ }
  };

  const handleToggleMemberStatus = (member) => {
    const isCurrentlyActive = member.is_active !== false;
    // B-75: archiving now ends their access and takes them off their teams,
    // and reactivating doesn't put them back on (server, clubs.js /status).
    if (isCurrentlyActive && !window.confirm(
      `Archive ${member.first_name} ${member.last_name}? They'll lose access to ${club?.name || 'the club'} and come off their teams. If they're a parent, they'll stop getting updates about their children here. You can reactivate them later, then add them back to a team from Edit.`
    )) return;
    updateMemberStatusMutation.mutate({
      memberId: member.id,
      is_active: !isCurrentlyActive
    });
  };

  // 17 Sept (Stuart): seven tabs wrapped into three rows on a phone. Now FOUR
  // groups, and the old tabs live on as sub-tabs — their keys are unchanged, so
  // `?tab=members` deep links and the tour's `#admin-tab-<key>` anchors still
  // land on the same screens. Single-screen groups (Sponsors, Reports) carry
  // the `admin-tab-<key>` id themselves.
  //   People   → Members · Teams
  //   Club     → Settings · Shop · Moderation  (red dot while anything is flagged)
  //   Sponsors → the Club Sponsors screen
  //   Reports  → the downloads screen ("reports" the key still means moderation —
  //              renamed in Aug so the downloads tab could have the obvious name)
  const groups = isClubAdmin ? [
    // B-58 slice 3: dot on People while join requests wait (the Admin door has the count).
    { key: 'people', label: 'People', icon: Users, alert: joinWaiting > 0, tabs: [
      { key: 'members', label: 'Members', count: membersInScope },
      { key: 'teams', label: 'Teams', count: teams.length },
    ] },
    { key: 'club', label: 'Club', icon: Settings, alert: openReports.length > 0 || newShopEnquiries > 0, tabs: [
      { key: 'settings', label: 'Settings' },
      { key: 'products', label: 'Shop', count: shopItemCount },
      { key: 'reports', label: 'Moderation', count: openReports.length },
    ] },
    { key: 'sponsors', label: 'Sponsors', icon: BarChart2, tabs: [{ key: 'sponsors', label: 'Sponsors' }] },
    // Reports: one pill per report (17 Sept) — Attendance is the old `exports`
    // key so `?tab=exports` and the tour's `#admin-tab-exports` still land here.
    { key: 'exports', label: 'Reports', icon: Download, tabs: [
      { key: 'exports', label: 'Attendance' },
      ...(cardsOn ? [{ key: 'report-membership', label: 'Membership & gate' }] : []),
      { key: 'report-sponsorship', label: 'Sponsorship' },
    ] },
  ] : [
    // B-11: a team manager's Club Admin is just Members — invite players,
    // coaches and parents to their own teams, manage their pending invites.
    { key: 'people', label: 'People', icon: Users, tabs: [{ key: 'members', label: 'Members', count: membersInScope }] },
    { key: 'exports', label: 'Reports', icon: Download, tabs: [{ key: 'exports', label: 'Reports' }] },
  ];
  // A deep link to a pill that isn't offered right now (e.g. ?tab=report-membership
  // with cards off) falls back to the first screen instead of a blank panel (QA r3 S2).
  const knownTab = groups.some(g => g.tabs.some(t => t.key === tab)) ? tab : groups[0].tabs[0].key;
  const activeGroup = groups.find(g => g.tabs.some(t => t.key === knownTab)) || groups[0];
  const subTabs = activeGroup.tabs.length > 1 ? activeGroup.tabs : null;

  // Accessible tab nav: arrow / Home / End move selection and focus (roving tabindex).
  const groupKeys = groups.map((g) => g.key);
  const onTabKeyDown = (e) => {
    const i = groupKeys.indexOf(activeGroup.key);
    let next;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = groupKeys[(i + 1) % groupKeys.length];
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = groupKeys[(i - 1 + groupKeys.length) % groupKeys.length];
    else if (e.key === 'Home') next = groupKeys[0];
    else if (e.key === 'End') next = groupKeys[groupKeys.length - 1];
    else return;
    e.preventDefault();
    setTab(groups.find(g => g.key === next).tabs[0].key);
    tabRefs.current[next]?.focus();
  };

  return (
    <div className="admin-page" style={{ '--accent': accentColor }}>
      <Navbar club={club} />
      {/* W-2: the admin tour continues here tab by tab (engine resumes from
          sessionStorage after the hop from the hub). Never auto-starts on
          this surface. */}
      <GuidedTour club={club} surface="admin" />

      <PageHeader
        icon={Settings}
        title="Club Admin"
        subtitle={<>{club?.name} · {user?.firstName} {user?.lastName}</>}
      />

      {/* Tab nav — four groups, then the group's sub-tabs (if it has more than one) */}
      <div className="admin-tabs-wrap">
        <div className="admin-tabs" role="tablist" aria-label="Club admin sections">
          {groups.map(({ key, label, icon: Icon, alert, tabs: gTabs }) => {
            const on = activeGroup.key === key;
            const single = gTabs.length === 1;
            return (
              <button key={key} id={single ? `admin-tab-${gTabs[0].key}` : `admin-group-${key}`} role="tab"
                aria-selected={on} aria-controls="admin-tabpanel"
                tabIndex={on ? 0 : -1}
                ref={(el) => { tabRefs.current[key] = el; }}
                className={`admin-tab ${on ? 'active' : ''}`}
                onClick={() => setTab(on ? tab : gTabs[0].key)}
                onKeyDown={onTabKeyDown}>
                <Icon size={16} /> {label}
                {alert && <span className="admin-tab-dot" aria-label="Needs attention" />}
              </button>
            );
          })}
        </div>
        {subTabs && (
          <div className="admin-subtabs" role="tablist" aria-label={`${activeGroup.label} sections`}>
            {subTabs.map(({ key, label, count }) => (
              <button key={key} id={`admin-tab-${key}`} role="tab"
                aria-selected={knownTab === key} aria-controls="admin-tabpanel"
                className={`admin-subtab ${knownTab === key ? 'active' : ''}`}
                onClick={() => setTab(key)}>
                {label}{count > 0 && <span className="tab-count">{count}</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="admin-content" id="admin-tabpanel" role="tabpanel"
        aria-labelledby={`admin-tab-${knownTab}`} tabIndex={0}>

        {/* ═══════════ MEMBERS TAB ═══════════ */}
        {knownTab === 'members' && (
          <div>
            <TabHead icon={Users} title="Members" count={membersInScope}>
              <button type="button" className={`members-link ${showArchivedMembers ? 'active' : ''}`} aria-pressed={showArchivedMembers}
                onClick={() => setShowArchivedMembers(v => !v)}>
                <Eye size={13} /> {showArchivedMembers ? 'Hide archived' : 'Show archived'}
              </button>
              {/* ONE Add button that opens a two-item menu (17 Sept tidy). */}
              <div className="members-add-wrap">
                <button className="btn btn-primary btn-sm" onClick={() => setShowAddMenu(v => !v)} aria-haspopup="menu" aria-expanded={showAddMenu}>
                  <UserPlus size={14} /> Add <ChevronDown size={13} />
                </button>
                {showAddMenu && (
                  <>
                    <div className="members-add-backdrop" onClick={() => setShowAddMenu(false)} />
                    <div className="members-add-menu" role="menu">
                      <button role="menuitem" onClick={() => { setShowAddMenu(false); resetInviteWizard(); }}>
                        <Mail size={14} /> Invite one person
                        <small>Email or a link, with their role and team</small>
                      </button>
                      <button role="menuitem" onClick={() => { setShowAddMenu(false); setShowBulkAdd(true); }}>
                        <Upload size={14} /> Add many from a list
                        <small>Paste emails or upload a spreadsheet</small>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </TabHead>

            {/* Tools row: filter icon (with a count when filters are set) · search. */}
            <div className="members-toolbar">
              <div className="members-toolbar-actions">
                <button
                  className={`btn btn-secondary btn-sm btn-icon members-filter-btn ${showFilters || activeFilterCount ? 'active' : ''}`}
                  title="Filter and sort" aria-label="Filter and sort" aria-expanded={showFilters}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Filter size={15} />
                  {activeFilterCount > 0 && <span className="members-filter-count">{activeFilterCount}</span>}
                </button>
              </div>
              <div className="members-search-wrap">
                <Search size={16} className="search-icon" />
                <input
                  type="text"
                  className="input members-search-input"
                  placeholder="Search by name or email..."
                  value={memberSearch}
                  onChange={e => setMemberSearch(e.target.value)}
                />
                {memberSearch && (
                  <button className="search-clear" onClick={() => setMemberSearch('')}>
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>

            {/* Filter bar */}
            {showFilters && (
              <div className="members-filters card">
                <div className="filter-group">
                  <label className="label">Role</label>
                  <select className="input input-sm" value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
                    <option value="">All roles</option>
                    {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                  </select>
                </div>
                <div className="filter-group">
                  <label className="label">Team</label>
                  <select className="input input-sm" value={teamFilter} onChange={e => setTeamFilter(e.target.value)}>
                    <option value="">All teams</option>
                    {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <div className="filter-group">
                  <label className="label">Sort by</label>
                  <select className="input input-sm" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                    {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                {cardsOn && (
                  <div className="filter-group">
                    <label className="label">Member status</label>
                    <select className="input input-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                      <option value="">Everyone</option>
                      <option value="community">Community members</option>
                      <option value="club">Club members</option>
                      <option value="staff">Club staff</option>
                    </select>
                  </div>
                )}
                {activeFilterCount > 0 && (
                  <button className="btn btn-ghost btn-sm members-filter-clear" onClick={() => { setRoleFilter(''); setTeamFilter(''); setSortBy('default'); setStatusFilter(''); }}>
                    <X size={13} /> Clear
                  </button>
                )}
              </div>
            )}

            {membersLoading && <div className="page-loading" style={{ minHeight: '20vh' }}><div className="spinner" /></div>}

            {/* Pending approval */}
            {pending.length > 0 && (
              <div className="admin-section">
                <div className="members-line members-line-pending">
                  <Clock size={14} /> <strong>{pending.length} waiting for approval</strong>
                </div>
                {/* WT-12: a waiting request offers Approve and Decline only: no
                    Edit (which could give it a team) and no Archive. Display
                    only; B-61's server rule still holds. */}
                <div className="members-list">
                  {pending.map(m => (
                    <MemberRow
                      key={m.id}
                      member={m}
                      accentColor={accentColor}
                      isPending
                      onApprove={isClubAdmin ? () => approveMutation.mutate(m.id) : undefined}
                      onDecline={isClubAdmin ? () => {
                        if (window.confirm(`Decline ${m.first_name}'s request? They'll see a neutral message and can't ask again for 30 days.`)) declineMutation.mutate(m.id);
                      } : undefined}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Approved members */}
            <div className="admin-section">
              {/* 17 Sept tidy: no banner heading — one slim line: count on the left,
                  quiet text actions on the right (Select acts on this list, so it lives here). */}
              {(narrowing.length > 0 || memberScope === 'teams') && (
                <div className="members-line">
                  <span>
                    {narrowing.length > 0 && <><strong>{visibleMembers.length}</strong> of {showArchivedMembers ? approved.length : membersInScope} shown</>}
                    {narrowing.map(n => <span key={n} className="members-line-note"> · {n}</span>)}
                    {memberScope === 'teams' && <span className="members-line-note">{narrowing.length ? ' · ' : ''}your teams only</span>}
                  </span>
                </div>
              )}
              <div className="members-list">
                {visibleMembers
                  .map(m => {
                    // WT-25: no pills to show (children; a read-only row with
                    // nothing on) → no `extra`, so the row has no empty footer.
                    const row = (
                      <MemberRow
                        key={m.id}
                        member={m}
                        accentColor={accentColor}
                        onEdit={isClubAdmin ? () => openEditMember(m) : undefined}
                        onToggleStatus={isClubAdmin ? () => handleToggleMemberStatus(m) : undefined}
                        extra={hasMemberPills(m, { enabled: cardsOn, canEdit: isClubAdmin }) ? <MemberPills clubSlug={clubSlug} member={m} enabled={cardsOn} canEdit={isClubAdmin} /> : null}
                      />
                    );
                    return row;
                  })}
                {!membersLoading && visibleMembers.length === 0 && (
                  <div className="empty-state">
                    {memberScope === 'teams'
                      ? 'You only see members of teams you manage — and you\'re not on a team roster yet. Ask a club admin to add you to your team in Club Admin → Teams.'
                      : 'No approved members yet.'}
                  </div>
                )}
              </div>
            </div>

            {/* Pending invites */}
            {inviteRows.length > 0 && (
              <div className="admin-section">
                {/* 17 Sept tidy: reference, not a daily task — one line, expands on tap. */}
                <button type="button" className={`members-line members-line-toggle ${showInvites ? 'open' : ''}`}
                  onClick={() => setShowInvites(v => !v)} aria-expanded={showInvites}>
                  <span><Mail size={14} /> <strong>{pendingInvites.length}</strong> pending {pendingInvites.length === 1 ? 'invite' : 'invites'}{expiredInvites.length > 0 ? ` · ${expiredInvites.length} expired in the last 30 days` : ''}</span>
                  <ChevronDown size={16} className="members-line-chevron" />
                </button>
                {showInvites && (
                <div className="members-list">
                  {inviteRows.map(inv => {
                    // PUBLIC_WEB_URL, not window.location.origin — in the native app
                    // the origin is 'localhost' and copied links are dead (F-010).
                    const invLink = `${PUBLIC_WEB_URL}/${clubSlug}/${inv.kind === 'coparent' ? 'coparent-accept' : 'invite'}/${inv.token}`; // co-parent invites have their own accept page
                    const handleCopyInviteLink = () => {
                      navigator.clipboard.writeText(invLink);
                      setCopiedInviteId(inv.id);
                      setTimeout(() => setCopiedInviteId(null), 2000);
                    };
                    const isEditing = editingInviteId === inv.id;
                    const p = inv.payload;
                    const isRich = !!(p && (p.clubAdmin || (Array.isArray(p.teamRoles) && p.teamRoles.length) || p.guardianIntent));
                    const isExpired = !inviteIsLive(inv);
                    return (
                    <div key={inv.id} className="card invite-row" style={{ padding: '12px 16px', marginBottom: 8 }}>
                      {/* Main row */}
                      <div className="member-row" style={{ marginBottom: isEditing ? 12 : 0 }}>
                        <div className="member-avatar invite-avatar"><Mail size={16} /></div>
                        <div className="member-info">
                          {/* W-16: link-only invites from bulk add carry the invitee's name. */}
                          <strong>{inv.email || [inv.payload?.invitee?.firstName, inv.payload?.invitee?.lastName].filter(Boolean).join(' ') || 'Open invite link'}</strong>
                          <span>{!inv.email && inv.payload?.invitee?.firstName ? 'Link only (no email) · ' : ''}{isExpired
                            ? <span style={{ color: 'var(--danger, #b42318)', fontWeight: 600 }}>Expired {new Date(inv.expires_at).toLocaleDateString()}{inv.email ? ' · Resend for 7 more days' : ' · link no longer works'}</span>
                            : inv.expires_at ? `Expires ${new Date(inv.expires_at).toLocaleDateString()}` : 'No expiry date'}</span>
                        </div>
                        <span className={`badge ${ROLE_COLORS[inv.role] || 'badge-silver'}`}>{describeInvitePayload(inv)}</span>
                        <div className="member-actions" style={{ gap: 4 }}>
                          {!isExpired && (<>
                          <button className="btn btn-ghost btn-sm" onClick={handleCopyInviteLink} title="Copy invite link">
                            <Copy size={14} /> {copiedInviteId === inv.id ? 'Copied!' : 'Copy link'}
                          </button>
                          <WhatsAppButton
                            text={inviteShareText(club?.name, invLink, inv.payload?.invitee?.firstName)}
                            clubSlug={clubSlug} className="btn btn-ghost btn-sm"
                          />
                          </>)}
                          {inv.email && (
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={() => resendInviteMutation.mutate(inv.id)}
                              disabled={resendInviteMutation.isPending}
                              title="Resend invite email"
                            >
                              <Mail size={14} /> {resentInviteId === inv.id ? 'Sent!' : 'Resend'}
                            </button>
                          )}
                          {!isRich && !isExpired && (
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={() => {
                                if (isEditing) { setEditingInviteId(null); }
                                else { setEditingInviteId(inv.id); setEditInviteForm({ email: inv.email || '', role: inv.role }); }
                              }}
                              title="Edit invite"
                            >
                              <Edit3 size={14} /> {isEditing ? 'Cancel' : 'Edit'}
                            </button>
                          )}
                          <button className="btn btn-ghost btn-sm btn-danger-text" onClick={() => revokeInviteMutation.mutate(inv.id)}>
                            <XCircle size={14} /> Revoke
                          </button>
                        </div>
                      </div>

                      {/* Inline edit form */}
                      {isEditing && (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', paddingLeft: 44, flexWrap: 'wrap' }}>
                          <input
                            type="email"
                            className="input"
                            style={{ flex: 1, minWidth: 180, height: 34, fontSize: 13 }}
                            placeholder="Email address"
                            value={editInviteForm.email}
                            onChange={e => setEditInviteForm(p => ({ ...p, email: e.target.value }))}
                          />
                          {!['sponsor', 'coparent'].includes(inv.kind) && (
                          <select
                            className="input"
                            style={{ height: 34, fontSize: 13, minWidth: 140 }}
                            value={editInviteForm.role}
                            onChange={e => setEditInviteForm(p => ({ ...p, role: e.target.value }))}
                          >
                            {/* TMs may only grant player/coach — the backend 403s anything else */}
                            {(isClubAdmin ? ROLES : ['player', 'coach']).map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                          </select>
                          )}
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => editInviteMutation.mutate({ inviteId: inv.id, data: ['sponsor', 'coparent'].includes(inv.kind) ? { email: editInviteForm.email } : editInviteForm })}
                            disabled={editInviteMutation.isPending}
                          >
                            {editInviteMutation.isPending ? 'Saving…' : 'Save'}
                          </button>
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ═══════════ ATTRIBUTION TAB ═══════════ */}
        {/* v33 — commercial tools locked until Marakin verifies the club.
            W-2 — instead of "we've been notified, nothing to do", this is the
            top of the sales funnel: make the revenue case and take the
            submission that fires the staff verification email. */}
        {knownTab === 'sponsors' && !clubVerified && (
          <div className="admin-section admin-sponsors-pitch" style={{ maxWidth: 680 }} data-tour="admin-sponsors-unverified">
            <TabHead icon={TrendingUp} title="Sponsors" />
            <h3 className="pitch-title">Turn your sponsors into money you can count</h3>
            <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text-secondary)' }}>
              <strong>Marakin brings sponsor money into verified clubs.</strong> Once you're verified you can add
              your club's sponsors and their member offers, hand each sponsor a scan-at-the-till QR flow, and
              show them exactly what backing {club?.name || 'the club'} brings through their door — visits,
              spend and discount honoured. Numbers like that are what turn a one-off donation into a sponsor
              who renews.
            </p>
            <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text-secondary)' }}>
              <strong>Verification is a two-minute form and, usually, a short call.</strong> Give us enough to
              confirm the club is real and that you speak for it, and your sponsorship tools switch on —
              normally within a day or two. Everything else (teams, members, fixtures, training, news) is
              already live and stays that way.
            </p>
            <VerificationForm clubSlug={clubSlug} club={club} />
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 18 }}>
              Questions? Use <strong>Report a problem</strong> in the menu and we'll get straight back to you.
            </p>
          </div>
        )}
        {knownTab === 'sponsors' && clubVerified && (
          <div>
            {/* Add sponsor form */}
            <div className="admin-section" data-tour="admin-sponsors-live">
              <TabHead icon={TrendingUp} title="Sponsors">
                {/* B-46: second door to the live dashboard, where admins manage sponsors. */}
                {membership?.role === 'admin' && (
                  <Link to={`/${clubSlug}/attribution`} className="btn btn-ghost btn-sm">
                    <BarChart2 size={14} /> Live dashboard
                  </Link>
                )}
                <button className="btn btn-primary btn-sm" onClick={openCreateSponsorWizard}>
                  <Plus size={14} /> Set up sponsorship
                </button>
              </TabHead>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 0 }}>
                Add a sponsor and their offer, then start it whenever you're ready — or schedule it for a future date. You can stop or archive a sponsorship anytime.
              </p>

            </div>

            {/* Sponsor accounts — list sponsors and allow creating accounts */}
            {sponsors.length > 0 && (
              <div className="admin-section">
                <div className="section-header" style={{ marginBottom: 16 }}>
                  <h2 className="admin-section-title"><TrendingUp size={16} /> Sponsorships</h2>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                    <input
                      type="checkbox"
                      checked={showArchivedSponsors}
                      onChange={(e) => setShowArchivedSponsors(e.target.checked)}
                      style={{ cursor: 'pointer' }}
                    />
                    <Eye size={14} />
                    Show archived
                  </label>
                </div>
                <div className="members-list">
                  {sponsors.map(s => {
                    const st = s.status || (s.is_active ? 'live' : 'archived');
                    const STATUS_META = {
                      draft:     { label: 'Draft',     bg: '#E8DFCE', fg: '#5c5647' },
                      scheduled: { label: 'Scheduled', bg: 'rgba(196,148,58,0.18)', fg: '#8A6220' },
                      live:      { label: 'Live',      bg: 'rgba(30,74,53,0.14)', fg: '#1E4A35' },
                      paused:    { label: 'Paused',    bg: '#EDE7DB', fg: '#6b5f4a' },
                      archived:  { label: 'Archived',  bg: '#E6E6E6', fg: '#888' },
                    };
                    const meta = STATUS_META[st] || STATUS_META.draft;
                    const dimmed = st !== 'live';
                    const busy = lifecycleSponsorMutation.isPending;
                    return (
                    <div key={s.id} className="member-row card" style={{ opacity: dimmed ? 0.78 : 1 }}>
                      <div className="member-avatar" style={{ background: 'rgba(196,148,58,0.10)', color: '#8A6220' }}>
                        {s.business_name?.charAt(0)}
                      </div>
                      <div className="member-info">
                        <strong>{s.business_name}</strong>
                        <span>
                          {s.offer_title || s.category || 'No offer set'}
                          {st === 'scheduled' && s.sponsorship_start ? ` · starts ${String(s.sponsorship_start).slice(0, 10)}` : ''}
                        </span>
                      </div>
                      <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', background: meta.bg, color: meta.fg, whiteSpace: 'nowrap' }}>
                        {meta.label}
                      </span>
                      <span className="badge badge-gold">{s.tier}</span>
                      <div className="member-actions">
                        {st !== 'archived' && (
                          <button className="btn btn-ghost btn-sm" title={st === 'live' ? 'Locked while live — click to stop it so you can edit' : 'Edit details'} onClick={() => handleEditClick(s)} disabled={busy}>
                            <Edit3 size={14} /> Edit
                          </button>
                        )}
                        {st === 'live' ? (
                          <button className="btn btn-ghost btn-sm" title="Stop — pauses the offer (hidden from members); you can start it again" onClick={() => handleSponsorLifecycle(s, 'stop')} disabled={busy}>
                            <Pause size={14} /> Stop
                          </button>
                        ) : st === 'archived' ? (
                          <button className="btn btn-ghost btn-sm" style={{ color: '#1E4A35' }} title="Bring back this sponsorship (as paused)" onClick={() => handleSponsorLifecycle(s, 'reactivate')} disabled={busy}>
                            <Archive size={14} /> Reactivate
                          </button>
                        ) : (
                          <button className="btn btn-primary btn-sm" title={st === 'scheduled' ? 'Start now (overrides the schedule)' : 'Start — make this offer live to members'} onClick={() => handleSponsorLifecycle(s, 'start')} disabled={busy}>
                            <Play size={14} /> {st === 'scheduled' ? 'Start now' : 'Start'}
                          </button>
                        )}
                        {st !== 'archived' && (
                          <button className="btn btn-ghost btn-sm" style={{ color: '#666' }} title="Archive — retire this sponsorship" onClick={() => handleSponsorLifecycle(s, 'archive')} disabled={busy}>
                            <Archive size={14} /> Archive
                          </button>
                        )}
                        {/* WT-11: say when the sponsor already has a login (linked_user_id),
                            and keep a quiet way to send a new link. */}
                        {s.linked_user_id ? (<>
                          <span className="sponsor-login-status" title="Someone already logs in for this sponsor to scan codes and see their results">
                            <CheckCircle size={14} /> Has a login
                          </span>
                          <button className="btn btn-ghost btn-sm" onClick={() => openCreateSponsorAccount(s)} title="Send a new login link, for example to someone new at the business">
                            <Mail size={14} /> Invite again
                          </button>
                        </>) : (
                          <button className="btn btn-secondary btn-sm" onClick={() => openCreateSponsorAccount(s)} title="Email the sponsor a link to set up their own login">
                            <UserPlus size={14} /> Invite to log in
                          </button>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Redemption report */}
            <div className="admin-section">
              {/* B-24: these are lifetime totals, unlike the sponsor
                  attribution page which defaults to 30 days. Label it until
                  B-36 gives this tab its own period filter. */}
              <h2 className="admin-section-title"><BarChart2 size={16} /> Redemption Report <span style={{ fontWeight: 400, fontSize: 13, color: 'var(--text-secondary)' }}>(all time)</span></h2>
              {reportBreakdown.length === 0 ? (
                <div className="empty-state">No redemptions recorded yet. Codes will appear here once members start using them.</div>
              ) : (
                <div className="report-table-wrap card" style={{ padding: 0, overflowX: 'auto' }}>
                  {/* WT-28: stacks on phones (AdminPage.css .redemption-table) — the
                      Total value column was clipped with no hint it scrolled. */}
                  <table className="report-table redemption-table">
                    <thead>
                      <tr>
                        <th>Sponsor</th>
                        <th className="num">Redemptions</th>
                        <th className="num">Unique Members</th>
                        <th className="num">Total Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportBreakdown.map((row, i) => (
                        <tr key={i}>
                          <td className="sponsor-cell"><strong>{row.business_name}</strong></td>
                          <td className="num" data-label="Redemptions"><strong style={{ color: accentColor }}>{row.total_redemptions}</strong></td>
                          <td className="num" data-label="Unique members">{row.unique_members}</td>
                          {/* W-12: endpoint renamed this to total_spend in the 19 Jun
                              attribution overhaul; total_value had shown "—" ever since.
                              WT-28: £1,114.10, not £1114.10 (formatGBP). */}
                          <td className="num" data-label="Total value">{formatGBP(row.total_spend)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══════════ SHOP TAB (B-49, v47) ═══════════ */}
        {/* Admins add / edit / hide / delete items themselves now (was a
            read-only list telling them to ask Marakin), plus the kit contact
            and the "Ask about this" enquiries. */}
        {knownTab === 'products' && <ShopManager clubSlug={clubSlug} TabHead={TabHead} />}

        {/* ═══════════ TEAMS TAB ═══════════ */}
        {knownTab === 'teams' && (
          <div>
            <div className="admin-section">
              <TabHead icon={Flag} title="Teams" count={teams.length}>
                <button type="button" className={`members-link ${showArchivedTeams ? 'active' : ''}`} aria-pressed={showArchivedTeams}
                  onClick={() => setShowArchivedTeams(v => !v)}>
                  <Eye size={13} /> {showArchivedTeams ? 'Hide archived' : 'Show archived'}
                </button>
                <button className="btn btn-primary btn-sm" onClick={() => {
                  if (!showTeamForm) setNewTeam(p => ({ ...p, sport: p.sport || club?.sport || '' }));
                  setShowTeamForm(v => !v);
                }}>
                  <Plus size={14} /> Add team
                </button>
              </TabHead>

              {showTeamForm && (
                <div className="card" style={{ marginBottom: 20 }}>
                  <h3 style={{ marginBottom: 16, fontSize: 16 }}>Add New Team</h3>
                  <div className="sponsor-form-grid">
                    <div className="form-group">
                      <label className="label">Team name *</label>
                      <input className="input" placeholder="e.g. Senior XV" value={newTeam.name}
                        onChange={e => setNewTeam(p => ({ ...p, name: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="label">Age group</label>
                      <input className="input" placeholder="e.g. Adult, U18, U15" value={newTeam.age_group}
                        onChange={e => setNewTeam(p => ({ ...p, age_group: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="label">Sport</label>
                      <input className="input" placeholder="e.g. Rugby Union" value={newTeam.sport}
                        onChange={e => setNewTeam(p => ({ ...p, sport: e.target.value }))} />
                    </div>
                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label className="label">Description (optional)</label>
                      <textarea className="input" rows={2} placeholder="Brief description of the team"
                        value={newTeam.description}
                        onChange={e => setNewTeam(p => ({ ...p, description: e.target.value }))} />
                    </div>
                  </div>
                  <div className="form-actions" style={{ marginTop: 16 }}>
                    <button className="btn btn-primary" style={{ background: accentColor }}
                      onClick={() => createTeamMutation.mutate(newTeam)}
                      disabled={!newTeam.name || createTeamMutation.isPending}>
                      {createTeamMutation.isPending ? 'Creating…' : 'Create team'}
                    </button>
                    <button className="btn btn-ghost" onClick={() => { setShowTeamForm(false); setNewTeam({ name: '', age_group: '', sport: '', description: '' }); }}>Cancel</button>
                  </div>
                </div>
              )}

              <div className="members-list">
                {(showArchivedTeams ? allTeams : teams).length === 0 && (
                  <div className="empty-state">No teams yet. Add your first team above — they'll appear in the fixture and training dropdowns.</div>
                )}
                {(showArchivedTeams ? allTeams : teams).map(t => (
                  <div
                    key={t.id}
                    className="member-row card"
                    style={{
                      opacity: !t.is_active ? 0.6 : 1,
                      filter: !t.is_active ? 'grayscale(50%)' : 'none'
                    }}
                  >
                    <div className="member-avatar" style={{ background: `${accentColor}18`, color: accentColor }}>
                      <Flag size={16} />
                    </div>
                    <div className="member-info">
                      <strong>{t.name}</strong>
                      <span>{[t.sport, t.age_group].filter(Boolean).join(' · ') || 'No sport / age group set'}</span>
                      {t.description && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t.description}</span>}
                    </div>
                    {!t.is_active && <span className="badge badge-silver">Archived</span>}
                    <div className="member-actions">
                      {/* B-1: admin-only, like the single add (Members → Edit → Team) */}
                      {isClubAdmin && t.is_active && (
                        <button className="btn btn-ghost btn-sm" title="Add members to this team" onClick={() => setAddingToTeam(t)}>
                          <UserPlus size={14} /> Add members
                        </button>
                      )}
                      <button className="btn btn-ghost btn-sm btn-icon" title="Edit team" onClick={() => handleEditTeam(t)}>
                        <Edit3 size={14} />
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        title={t.is_active ? 'Archive team' : 'Reactivate team'}
                        onClick={() => handleToggleTeamStatus(t)}
                        style={{ color: t.is_active ? '#666' : '#1E4A35' }}
                      >
                        <Archive size={14} />
                        {t.is_active ? 'Archive' : 'Reactivate'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ═══════════ REPORTS TAB (downloads) ═══════════ */}
        {knownTab === 'exports' && (
          <>
            <TabHead icon={Users} title="Attendance" />
            <div className="admin-section">
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 4 }}>
                {/* WT-23: say it includes the register (B-27), not just the replies. */}
                Who was asked, who said they were coming, who never answered, and who actually
                turned up (from the event&rsquo;s register) — one row per person per event, plus a
                per-person summary with a Going&nbsp;% and an Attended&nbsp;% for each member.
              </p>
              <div className="form-row-2col" style={{ marginTop: 12 }}>
                <div className="form-group">
                  <label className="label">From</label>
                  <input className="input" type="date" value={attFrom}
                    onChange={e => setAttFrom(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="label">To</label>
                  <input className="input" type="date" value={attTo}
                    onChange={e => setAttTo(e.target.value)} />
                </div>
              </div>
              <div className="form-row-2col">
                <div className="form-group">
                  <label className="label">Team</label>
                  <select className="input" value={attTeam} onChange={e => setAttTeam(e.target.value)}>
                    <option value="">{isClubAdmin ? 'All teams' : 'All my teams'}</option>
                    {(isClubAdmin ? allTeams : invitableTeams).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="label">Type</label>
                  <select className="input" value={attType} onChange={e => setAttType(e.target.value)}>
                    <option value="">Fixtures and training</option>
                    <option value="match">Fixtures only</option>
                    <option value="training">Training only</option>
                  </select>
                </div>
              </div>
              {isClubAdmin && (
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: 14, marginTop: 4 }}>
                  <input type="checkbox" checked={attEmails} style={{ cursor: 'pointer', marginTop: 3 }}
                    onChange={e => setAttEmails(e.target.checked)} />
                  <span>
                    Include members' email addresses
                    <span style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)' }}>
                      Off by default — a spreadsheet of contact details is easy to forward
                      by accident. Children never have one.
                    </span>
                  </span>
                </label>
              )}
              <ReportDownload
                url={`/clubs/${clubSlug}/reports/attendance/export`}
                params={{
                  from: attFrom || undefined, to: attTo || undefined,
                  teamId: attTeam || undefined, eventType: attType || undefined,
                  includeEmails: attEmails ? '1' : undefined,
                }}
                fallbackName="attendance"
                csvSheets={[{ index: 0, label: 'CSV — every response' }, { index: 1, label: 'CSV — by person' }]}
                note="Excel has both sheets plus the notes on how the numbers are worked out. Leave the dates blank for everything on record."
              />
            </div>

          </>
        )}

        {/* v35: membership & gate — club admins, only once cards are on. */}
        {knownTab === 'report-membership' && isClubAdmin && cardsOn && (
          <MembershipReport clubSlug={clubSlug} />
        )}

        {knownTab === 'report-sponsorship' && isClubAdmin && (
          <>
            <TabHead icon={BarChart2} title="Sponsorship performance">
              {/* B-46: the live dashboard's door (was the navbar's "Sponsorship").
                  role 'admin' only: /attribution turns away anyone else (e.g.
                  platform staff who are ordinary members here). */}
              {clubVerified && membership?.role === 'admin' && (
                <Link to={`/${clubSlug}/attribution`} className="btn btn-primary btn-sm" data-tour="admin-sponsorship-dashboard">
                  <TrendingUp size={14} /> Live dashboard
                </Link>
              )}
            </TabHead>
            {/* v33: sponsorship export is a commercial tool — hidden until the
                club is verified (the endpoint 403s regardless). */}
            {!clubVerified && (
            <div className="admin-section">
              <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
                Switches on once Marakin verifies your club — {club?.verification_submitted_at
                  ? 'your details are with us and we\'ll be in touch shortly.'
                  : <><Link to={`/${clubSlug}/admin?tab=sponsors`} onClick={() => setTab('sponsors')}>submit your verification details</Link> to unlock it.</>}
              </p>
            </div>
            )}
            {clubVerified && (
            <div className="admin-section">
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 4 }}>
                Redemptions, member spend, discount given and what each sponsor's fee has
                returned — plus every individual redemption. Useful when a sponsor asks what
                they got for their money, or at renewal.
              </p>
              {/* B-48 / UC-8: period chips; exact From/To dates under a fold.
                  A chip clears the dates (the server ignores period when dates are set). */}
              <div className="pill-group" style={{ justifyContent: 'flex-start', margin: '12px 0 8px' }}>
                {[['7d', '7 days'], ['30d', '30 days'], ['90d', '90 days'], ['all', 'All time']].map(([val, label]) => (
                  <button
                    key={val}
                    type="button"
                    className={`pill-btn ${spPeriod === val && !spFrom && !spTo ? 'active' : ''}`}
                    aria-pressed={spPeriod === val && !spFrom && !spTo}
                    onClick={() => { setSpPeriod(val); setSpFrom(''); setSpTo(''); setSpCustom(false); }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                aria-expanded={spCustom}
                onClick={() => setSpCustom(v => !v)}
              >
                Custom dates{(spFrom || spTo) && !spCustom ? ' (in use)' : ''} <ChevronDown size={14} style={{ transform: spCustom ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
              </button>
              {spCustom && (
              <div className="form-row-2col" style={{ marginTop: 8 }}>
                <div className="form-group">
                  <label className="label">From</label>
                  <input className="input" type="date" value={spFrom} onChange={e => setSpFrom(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="label">To</label>
                  <input className="input" type="date" value={spTo} onChange={e => setSpTo(e.target.value)} />
                </div>
              </div>
              )}
              <ReportDownload
                url={`/clubs/${clubSlug}/reports/sponsorship/export`}
                params={{ period: spPeriod, from: spFrom || undefined, to: spTo || undefined }}
                fallbackName="sponsorship"
                csvSheets={[{ index: 0, label: 'CSV — by sponsor' }, { index: 1, label: 'CSV — redemptions' }]}
                note="Excel contains both sheets. Pick a period, or use Custom dates for an exact window (a sponsor's contract dates, a season). Unlike the on-screen report, the download isn't capped — you get every redemption in the window."
              />
            </div>
            )}
          </>
        )}

        {/* ═══════════ MODERATION TAB (UGC) ═══════════ */}
        {knownTab === 'reports' && (
          <div className="admin-section">
            <TabHead icon={AlertTriangle} title="Moderation" count={openReports.length} />
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>
              Chat messages flagged by members. <strong>Remove</strong> deletes the message for
              everyone; <strong>Dismiss</strong> keeps it and closes the report. Either way the
              report is kept on record. Kit Swap chats are private between two members — you can
              open one only when a message in it has been reported.
            </p>
            {openReports.length === 0 && (
              <div className="empty-state">No open reports — nothing needs your attention. 🎉</div>
            )}
            <div className="members-list">
              {openReports.map(r => (
                <div key={r.id} className="card" style={{ padding: 16 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'baseline', marginBottom: 6 }}>
                    <strong style={{ fontSize: 14 }}>{r.message_author_name || 'Unknown member'}</strong>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      in {r.event_title || 'a deleted event'} · reported by {r.reporter_name || 'a member'} · {new Date(r.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <blockquote style={{
                    margin: '0 0 10px', padding: '8px 12px', borderLeft: '3px solid var(--gold)',
                    background: 'var(--surface-2)', borderRadius: 6, fontSize: 14, whiteSpace: 'pre-wrap',
                  }}>
                    {r.message_body}
                  </blockquote>
                  {r.reason && (
                    <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 10 }}>
                      Reporter's note: {r.reason}
                    </p>
                  )}
                  {r.kind === 'kit' && <KitReportThread clubSlug={clubSlug} reportId={r.id} />}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      disabled={resolveReportMutation.isPending}
                      onClick={() => resolveReportMutation.mutate({ reportId: r.id, action: 'dismiss' })}
                    >
                      <CheckCircle size={14} /> Dismiss
                    </button>
                    <button
                      className="btn btn-sm"
                      style={{ background: 'var(--danger)', color: '#fff' }}
                      disabled={resolveReportMutation.isPending}
                      onClick={() => {
                        if (window.confirm('Remove this message for everyone? This can\'t be undone.')) {
                          resolveReportMutation.mutate({ reportId: r.id, action: 'remove' });
                        }
                      }}
                    >
                      <Trash2 size={14} /> Remove message
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══════════ SETTINGS TAB ═══════════ */}
        {knownTab === 'settings' && (
          <div className="admin-section">
            <TabHead icon={Settings} title="Club Settings" />

            {/* Logo upload */}
            <div className="card settings-card">
              <h3 style={{ marginBottom: 4 }}>Club Badge / Logo</h3>
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>
                Upload a PNG or JPG badge. It will appear in the club hero and throughout the hub.
              </p>
              <div className="logo-upload-row">
                <div className="logo-preview-box" style={{ background: accentColor }}>
                  {(logoPreview || club?.logo_url)
                    ? <img src={logoPreview || club?.logo_url} alt="Club logo" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 12 }} />
                    : <span style={{ fontSize: 32, fontWeight: 900, color: '#fff' }}>{club?.name?.charAt(0)}</span>
                  }
                </div>
                <div className="logo-upload-actions">
                  <label className="btn btn-secondary" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <Upload size={14} /> Choose image
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoChange} />
                  </label>
                  {logoPreview && (
                    <>
                      <button className="btn btn-primary" onClick={handleLogoSave} disabled={logoSaving}>
                        {logoSaving ? 'Saving…' : 'Save logo'}
                      </button>
                      <button className="btn btn-ghost" onClick={() => setLogoPreview(null)}>Cancel</button>
                    </>
                  )}
                  {!logoPreview && club?.logo_url && (
                    <button className="btn btn-ghost" onClick={async () => {
                      await axios.patch(`/clubs/${clubSlug}/settings`, { logo_url: null });
                      qc.invalidateQueries({ queryKey: ['club', clubSlug] });
                    }}>Remove logo</button>
                  )}
                </div>
              </div>
              {logoPreview && <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>Preview shown above — click Save to apply.</p>}
            </div>

            {/* ─── Club Identity ──────────────────────────────────── */}
            <div className="card settings-card" style={{ marginTop: 16 }}>
              <h3 style={{ marginBottom: 4 }}>Club Identity</h3>
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>
                Shown in the club hero at the top of your hub — nickname, founded year,
                contact phone and website.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                <div className="form-group">
                  <label className="label">Nickname</label>
                  <input className="input" placeholder="e.g. The Ravens"
                    value={identityForm.nickname}
                    onChange={e => { setIdentityForm(p => ({ ...p, nickname: e.target.value })); setIdentitySaved(false); }} />
                </div>
                <div className="form-group">
                  <label className="label">Founded (year)</label>
                  <input className="input" type="number" min="1800" max={new Date().getFullYear()} placeholder="e.g. 1893"
                    value={identityForm.founded_year}
                    onChange={e => { setIdentityForm(p => ({ ...p, founded_year: e.target.value })); setIdentitySaved(false); }} />
                </div>
                <div className="form-group">
                  <label className="label">Club phone</label>
                  <input className="input" type="tel" placeholder="e.g. 01275 123456"
                    value={identityForm.phone}
                    onChange={e => { setIdentityForm(p => ({ ...p, phone: e.target.value })); setIdentitySaved(false); }} />
                </div>
                <div className="form-group">
                  <label className="label">Website</label>
                  <input className="input" placeholder="e.g. www.nailseabackwellrfc.co.uk"
                    value={identityForm.website}
                    onChange={e => { setIdentityForm(p => ({ ...p, website: e.target.value })); setIdentitySaved(false); }} />
                </div>
                <div className="form-group">
                  <label className="label">Sport</label>
                  <input className="input" placeholder="e.g. Rugby Union"
                    value={identityForm.sport}
                    onChange={e => { setIdentityForm(p => ({ ...p, sport: e.target.value })); setIdentitySaved(false); }} />
                </div>
                <div className="form-group">
                  <label className="label">Auto RSVP deadline</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input className="input" type="number" min={0} max={30} style={{ maxWidth: 90 }}
                      value={identityForm.rsvp_deadline_days}
                      onChange={e => { setIdentityForm(p => ({ ...p, rsvp_deadline_days: e.target.value })); setIdentitySaved(false); }} />
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>days before the event</span>
                  </div>
                  <span className="form-hint" style={{ color: 'var(--text-muted, #6B7280)' }}>
                    New events get a "Respond by" date this many days ahead (editable per event). Non-responders are reminded 24h before the deadline. 0 = off.
                  </span>
                </div>
              </div>

              {!CLUB_COLOURS_LOCKED && (<>
              <label className="label" style={{ marginTop: 14, display: 'block' }}>Club colours</label>
              <p style={{ fontSize: 12, color: 'var(--text-muted, #6B7280)', marginTop: -2, marginBottom: 10 }}>
                Primary drives the accent throughout the hub. Add up to four — any you don't set use Marakin defaults.
              </p>
              <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                {CLUB_COLOR_FIELDS.slice(0, identityColorCount).map(([key, lbl], i) => (
                  <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 5, position: 'relative' }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>{lbl}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="color" value={identityForm[key]}
                        onChange={e => { setIdentityForm(p => ({ ...p, [key]: e.target.value })); setIdentitySaved(false); }}
                        style={{ width: 42, height: 42, padding: 2, border: '1px solid var(--border, #E5E7EB)', borderRadius: 8, cursor: 'pointer', background: 'none' }} />
                      <input className="input" style={{ width: 92, fontFamily: 'monospace', textTransform: 'uppercase' }}
                        value={identityForm[key]}
                        onChange={e => {
                          let v = e.target.value.trim();
                          if (v && !v.startsWith('#')) v = '#' + v;
                          setIdentityForm(p => ({ ...p, [key]: v })); setIdentitySaved(false);
                        }}
                        maxLength={7} />
                      {i === identityColorCount - 1 && i > 0 && (
                        <button type="button" aria-label={`Remove ${lbl} colour`}
                          onClick={() => { setIdentityColorCount(identityColorCount - 1); setIdentitySaved(false); }}
                          style={{ width: 22, height: 22, borderRadius: '50%', border: 'none', background: '#999', color: '#fff', fontSize: 14, lineHeight: 1, cursor: 'pointer', padding: 0, flexShrink: 0 }}>×</button>
                      )}
                    </div>
                  </div>
                ))}
                {identityColorCount < 4 && (
                  <button type="button" onClick={() => setIdentityColorCount(identityColorCount + 1)} aria-label="Add another colour"
                    style={{ alignSelf: 'flex-end', width: 42, height: 42, borderRadius: 8, border: '1px dashed var(--border, #bbb)', background: 'transparent', color: 'var(--text-secondary, #666)', fontSize: 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                )}
              </div>
              </>)}

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16 }}>
                <button className="btn btn-primary" disabled={identitySaving} onClick={async () => {
                  setIdentitySaving(true);
                  try {
                    const hex = /^#[0-9A-Fa-f]{6}$/;
                    // Colours within the visible count are saved; removed ones (beyond the
                    // count) are cleared to null so the hub stops showing their swatch.
                    const colourPayload = {};
                    CLUB_COLOR_FIELDS.forEach(([key], i) => {
                      if (i >= identityColorCount) colourPayload[key] = null;
                      else if (hex.test(identityForm[key])) colourPayload[key] = identityForm[key];
                    });
                    await axios.patch(`/clubs/${clubSlug}/settings`, {
                      nickname: identityForm.nickname.trim() || null,
                      founded_year: identityForm.founded_year || null,
                      phone: identityForm.phone.trim() || null,
                      website: identityForm.website.trim() || null,
                      sport: identityForm.sport.trim() || null,
                      rsvp_deadline_days: identityForm.rsvp_deadline_days === '' ? 3 : Number(identityForm.rsvp_deadline_days),
                      ...colourPayload,
                    });
                    qc.invalidateQueries({ queryKey: ['club', clubSlug] });
                    setIdentitySaved(true);
                  } finally {
                    setIdentitySaving(false);
                  }
                }}>
                  {identitySaving ? 'Saving…' : 'Save identity'}
                </button>
                {identitySaved && <span style={{ fontSize: 13, color: 'var(--success, #2e7d32)', fontWeight: 700 }}>✓ Saved</span>}
              </div>
            </div>

            {/* ─── Home Ground ────────────────────────────────────── */}
            <div className="card settings-card" style={{ marginTop: 16 }}>
              <h3 style={{ marginBottom: 4 }}>Home Ground</h3>
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>
                This is where your community will go for home matches. We'll show a map and
                live weather forecast on every home fixture once this is set.
              </p>

              {/* Mapbox autocomplete search — primary input. User types
                  the club's name, postcode, or street and picks from
                  suggestions. Pinning the coords this way avoids the
                  postcode-centroid imprecision we hit with OSM. */}
              <div className="form-group" style={{ position: 'relative' }}>
                <label className="label">Find your ground</label>
                <p style={{ fontSize: 12, color: 'var(--text-muted, #6B7280)', marginTop: -4, marginBottom: 8 }}>
                  Type the club name, postcode, or address. Pick the right match from the suggestions.
                </p>
                <input
                  className="input"
                  placeholder="e.g. Nailsea and Backwell Rugby Football Club, or BS48 4BY"
                  value={searchQuery}
                  onChange={e => handleSearchChange(e.target.value)}
                  onFocus={() => searchResults.length > 0 && setSearchOpen(true)}
                  onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
                  autoComplete="off"
                />
                {searchLoading && (
                  <span style={{ position: 'absolute', right: 14, top: 60, fontSize: 12, color: 'var(--text-muted, #6B7280)' }}>
                    Searching…
                  </span>
                )}

                {/* Autocomplete dropdown */}
                {searchOpen && searchResults.length > 0 && (
                  <div style={{
                    marginTop: 4,
                    background: '#fff',
                    border: '1px solid var(--border, #E5E7EB)',
                    borderRadius: 8,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                    maxHeight: 320,
                    overflowY: 'auto',
                  }}>
                    {searchResults.map((c, i) => {
                      const isPOI = c.kind === 'poi';
                      const isPostcodeArea = c.kind === 'postcode_area';
                      return (
                        <button
                          key={i}
                          type="button"
                          onMouseDown={() => handlePickSearchResult(c)}
                          style={{
                            display: 'block',
                            width: '100%',
                            textAlign: 'left',
                            background: 'transparent',
                            border: 'none',
                            borderBottom: i < searchResults.length - 1 ? '1px solid var(--border-light, #F3F4F6)' : 'none',
                            padding: '12px 14px',
                            cursor: 'pointer',
                            whiteSpace: 'normal',
                          }}
                          onMouseOver={e => e.currentTarget.style.background = 'var(--surface-2, #F4EFE6)'}
                          onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                            <span style={{
                              fontSize: 10,
                              fontWeight: 700,
                              padding: '1px 6px',
                              borderRadius: 3,
                              background: isPOI ? 'var(--marakin-accent, #1E4A35)' : (isPostcodeArea ? '#9CA3AF' : '#6B7280'),
                              color: '#fff',
                              letterSpacing: 0.5,
                            }}>
                              {isPOI ? 'VENUE' : (isPostcodeArea ? 'POSTCODE' : 'ADDRESS')}
                            </span>
                            {c.name && c.name !== c.displayName && (
                              <span style={{ fontSize: 14, fontWeight: 600 }}>{c.name}</span>
                            )}
                          </div>
                          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>📍 {c.displayName}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {searchError && (
                  <p style={{ fontSize: 13, color: '#B45309', marginTop: 8 }}>{searchError}</p>
                )}

                {/* Confirmed-state banner once a location is pinned */}
                {groundForm.lat && groundForm.lng && groundForm.resolvedAddress && (
                  <div style={{
                    marginTop: 12,
                    padding: '10px 12px',
                    background: 'rgba(30, 74, 53, 0.08)',
                    borderLeft: '3px solid var(--marakin-accent, #1E4A35)',
                    borderRadius: 6,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 8,
                  }}>
                    <div>
                      <p style={{ fontSize: 13, color: 'var(--marakin-accent, #1E4A35)', fontWeight: 600, marginBottom: 2 }}>
                        ✓ Map location pinned
                      </p>
                      <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                        {groundForm.resolvedAddress}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={handleClearGroundCoords}
                      style={{ flexShrink: 0 }}
                    >
                      Change
                    </button>
                  </div>
                )}
              </div>

              {/* Display name — separate from the search, so the user can
                  call it "West End Park" even if they searched by the full
                  club name. Shown on event cards. */}
              <div className="form-group" style={{ marginTop: 16 }}>
                <label className="label">Display name <span className="optional">(what to call this venue on event cards)</span></label>
                <input
                  className="input"
                  placeholder="e.g. West End Park"
                  value={groundForm.name}
                  onChange={e => setGroundForm(p => ({ ...p, name: e.target.value }))}
                />
              </div>

              <div style={{ marginTop: 16 }}>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveGround}
                  disabled={groundSaving || !groundForm.name || !groundForm.lat}
                >
                  {groundSaving ? 'Saving…' : 'Save home ground'}
                </button>
                {/* WT-6: Save needs a map pin, which only comes from picking a
                    suggestion. A ground with a name but no pin (the Testvale seed,
                    older clubs) looked saved, so say what's missing. */}
                {!groundForm.lat && (
                  <p style={{ fontSize: 12, color: 'var(--text-muted, #6B7280)', marginTop: 8 }}>
                    {groundForm.name
                      ? 'Not on the map yet. Search for your ground above and pick a suggestion to enable Save.'
                      : 'Search for your ground above and pick a suggestion to enable Save.'}
                  </p>
                )}
              </div>
            </div>

            {/* ─── Membership cards (v35) ─────────────────────────── */}
            <MembershipSettingsCard clubSlug={clubSlug} />

            {/* ─── Kit Swap (B-45, v36) ────────────────────────────── */}
            <KitSwapSettingsCard clubSlug={clubSlug} />

            {/* ─── Fixture Finder opt-out (B-43.1, v45) ───────────── */}
            {isClubAdmin && <FixtureFinderSettingsCard clubSlug={clubSlug} />}
          </div>
        )}
      </div>

      {/* ═══════════ EDIT MEMBER MODAL (role + team + jersey; remove at the bottom) ═══════════ */}
      {editingMember && (
        <div className="modal-overlay" onClick={() => setEditingMember(null)}>
          <div className="modal-card modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit member</h3>
              <button className="btn btn-ghost btn-icon" onClick={() => setEditingMember(null)} aria-label="Close"><X size={18} /></button>
            </div>
            <p className="modal-subtitle">{editingMember.first_name} {editingMember.last_name}{editingMember.email ? ` · ${editingMember.email}` : ''}</p>
            <div className="invite-form">
              <div className="form-group">
                <label className="label">Role</label>
                <div className="role-picker">
                  {ROLES.map(r => (
                    <button
                      key={r}
                      type="button"
                      className={`role-option ${editRole === r ? 'selected' : ''}`}
                      onClick={() => setEditRole(r)}
                      style={editRole === r ? { borderColor: accentColor, background: `${accentColor}10` } : {}}
                    >
                      <Shield size={14} style={editRole === r ? { color: accentColor } : {}} />
                      <span>{ROLE_LABELS[r]}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="form-row-2col">
                <div className="form-group">
                  <label className="label">Team</label>
                  <select
                    className="input"
                    value={editMemberForm.team_id}
                    onChange={e => setEditMemberForm(p => ({ ...p, team_id: e.target.value }))}
                  >
                    <option value="">No team</option>
                    {teams.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="label">Jersey number</label>
                  <input
                    className="input"
                    type="number"
                    placeholder="e.g. 7"
                    value={editMemberForm.jersey_number}
                    onChange={e => setEditMemberForm(p => ({ ...p, jersey_number: e.target.value }))}
                  />
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-primary"
                disabled={editSaving || (!editRoleChanged && !editDetailsChanged)}
                onClick={handleSaveEditMember}
              >
                {editSaving ? 'Saving...' : 'Save changes'}
              </button>
              <button className="btn btn-ghost" onClick={() => setEditingMember(null)}>Cancel</button>
              <button
                className="btn btn-ghost btn-danger-text"
                style={{ marginLeft: 'auto' }}
                onClick={() => { const m = editingMember; setEditingMember(null); setRemovingMember(m); }}
              >
                <Trash2 size={14} /> Remove from the club…
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ REMOVE MEMBER MODAL ═══════════ */}
      {removingMember && (
        <div className="modal-overlay" onClick={() => setRemovingMember(null)}>
          <div className="modal-card modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Remove Member</h3>
              <button className="btn btn-ghost btn-icon" onClick={() => setRemovingMember(null)}><X size={18} /></button>
            </div>
            <div className="remove-confirm">
              <AlertTriangle size={32} style={{ color: 'var(--danger)' }} />
              <p>Are you sure you want to remove <strong>{removingMember.first_name} {removingMember.last_name}</strong> from the club?</p>
              <p className="remove-note">They will also be removed from all teams. This cannot be undone.</p>
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-danger"
                disabled={removeMemberMutation.isPending}
                onClick={() => removeMemberMutation.mutate(removingMember.id)}
              >
                {removeMemberMutation.isPending ? 'Removing...' : 'Remove member'}
              </button>
              <button className="btn btn-ghost" onClick={() => setRemovingMember(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ INVITE MODAL ═══════════ */}
      {showBulkAdd && (
        <BulkAddMembers
          clubSlug={clubSlug}
          clubName={club?.name}
          teams={invitableTeams}
          isClubAdmin={isClubAdmin}
          accentColor={accentColor}
          onClose={(didSend) => {
            setShowBulkAdd(false);
            if (didSend) {
              qc.invalidateQueries({ queryKey: ['invites', clubSlug] });
              qc.invalidateQueries({ queryKey: ['members', clubSlug] });
            }
          }}
        />
      )}

      {showInviteModal && (
        <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="modal-card modal-md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Invite Members</h3>
              <button className="btn btn-ghost btn-icon" onClick={() => setShowInviteModal(false)}><X size={18} /></button>
            </div>
            <p className="modal-subtitle">Pick what this person will do at the club. They can hold more than one hat — pick a team role, mark them as a parent/guardian, or both. Add an email to send the invite, or leave it blank for a shareable link.</p>

            <div className="invite-form">
              <div className="form-group">
                <label className="label">Email <span className="optional">(optional)</span></label>
                <input className="input" type="email" placeholder="Leave blank for an open invite link"
                  value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} />
              </div>

              <div className="form-group">
                <label className="label">In a team, they’ll be a…</label>
                <select className="input" value={inviteTeamRole}
                  onChange={e => { setInviteTeamRole(e.target.value); if (e.target.value === 'none') setInviteTeamId(''); }}>
                  <option value="none">Not in a team</option>
                  <option value="player">Player</option>
                  <option value="coach">Coach</option>
                  {isClubAdmin && <option value="team_manager">Team manager</option>}
                </select>
              </div>

              {inviteTeamRole !== 'none' && (
                <div className="form-group">
                  <label className="label">Which team?</label>
                  <select className="input" value={inviteTeamId} onChange={e => setInviteTeamId(e.target.value)}>
                    <option value="">-- Select a team --</option>
                    {invitableTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                  {invitableTeams.length === 0 && <p className="invite-note">{isClubAdmin ? 'No teams yet — create one in the Teams tab first.' : 'You don\'t manage any teams yet — ask a club admin to add you as team manager.'}</p>}
                </div>
              )}

              <div className="form-group">
                {/* W-17: width/justify/textAlign pinned — the rows drifted centre. */}
                <label className="checkbox-row" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', gap: 8, cursor: 'pointer', width: '100%', textAlign: 'left' }}>
                  <input type="checkbox" checked={inviteGuardian} onChange={e => setInviteGuardian(e.target.checked)} style={{ marginTop: 3, flexShrink: 0 }} />
                  <span style={{ flex: 1 }}>They look after a child here <span className="optional">(Parent / Guardian — they’ll connect their child after joining)</span></span>
                </label>
              </div>

              {isClubAdmin && (
                <div className="form-group">
                  <label className="checkbox-row" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', gap: 8, cursor: 'pointer', width: '100%', textAlign: 'left' }}>
                    <input type="checkbox" checked={inviteClubAdmin} onChange={e => setInviteClubAdmin(e.target.checked)} style={{ marginTop: 3, flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>Club admin <span className="optional">(full access to club settings, members and sponsors)</span></span>
                  </label>
                </div>
              )}

              <div className="invite-summary" style={{ padding: '10px 12px', borderRadius: 8, background: 'var(--cream, #F4EFE6)', fontSize: 14, marginTop: 4 }}>
                <strong>They’ll join as:</strong> {inviteSummary}
              </div>

              <button className="btn btn-primary" style={{ background: accentColor }}
                onClick={handleGenerateInvite} disabled={inviteMutation.isPending}>
                {inviteMutation.isPending ? 'Generating...' : <><Link2 size={14} /> {inviteEmail ? 'Send invite' : 'Generate invite link'}</>}
              </button>
            </div>

            {inviteError && <div className="invite-error"><AlertTriangle size={14} /> {inviteError}</div>}

            {generatedLink && (
              <div className="invite-result">
                <label className="label">Share this link</label>
                <div className="invite-link-row">
                  <input className="input" readOnly value={generatedLink} />
                  <button className="btn btn-secondary" onClick={handleCopyLink}>
                    {linkCopied ? <><CheckCircle size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
                  </button>
                  <WhatsAppButton text={inviteShareText(club?.name, generatedLink)} clubSlug={clubSlug} />
                </div>
                <p className="invite-note">This link expires in 7 days. WhatsApp opens with a ready-made message — just pick who to send it to.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════════ EDIT SPONSOR MODAL ═══════════ */}
      {sponsorWizard.open && (() => {
        const isEdit = sponsorWizard.mode === 'edit';
        const totalSteps = isEdit ? 3 : 4;
        const step = sponsorWizard.step;
        const saving = createSponsorMutation.isPending || updateSponsorMutation.isPending;
        const STEP_TITLES = ['The business', 'The offer', 'Cost & dates', 'Go live'];
        const offerLen = sponsorForm.offer_title.trim().length;
        const termsLen = sponsorForm.offer_description.trim().length;
        const offerTooLong = offerLen > OFFER_MAX || termsLen > TERMS_MAX;
        const badge = discountLabel(sponsorForm.discount_type, sponsorForm.discount_rate);
        const mismatch = offerMismatch({ type: sponsorForm.discount_type, rate: sponsorForm.discount_rate, texts: [sponsorForm.offer_title, sponsorForm.offer_description] });
        const canNext = step === 1 ? !!sponsorForm.business_name.trim() : step === 2 ? !offerTooLong : true;
        const counter = (n, max) => (
          <span className={`sponsor-count${n > max ? ' over' : ''}`} aria-live="polite">{n} / {max}</span>
        );
        const scheduleNoDate = !isEdit && step === 4 && sponsorWizard.goLive === 'schedule' && !sponsorForm.sponsorship_start;
        const goStep = (n) => setSponsorWizard(p => ({ ...p, step: Math.max(1, Math.min(totalSteps, n)) }));
        return (
        <div className="modal-overlay" onClick={closeSponsorWizard}>
          <div className="modal-card modal-md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{isEdit ? 'Edit sponsorship' : 'Set up sponsorship'}</h3>
              <button className="btn btn-ghost btn-icon" onClick={closeSponsorWizard}><X size={18} /></button>
            </div>

            {/* Step indicator */}
            <div style={{ display: 'flex', gap: 8, margin: '4px 0 18px' }}>
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div key={i} style={{ flex: 1 }}>
                  <div style={{ height: 4, borderRadius: 2, background: i < step ? '#1E4A35' : '#E8DFCE' }} />
                  <div style={{ fontSize: 11, marginTop: 5, fontWeight: i + 1 === step ? 700 : 500, color: i + 1 === step ? '#1E4A35' : 'var(--text-secondary)' }}>
                    {i + 1}. {STEP_TITLES[i]}
                  </div>
                </div>
              ))}
            </div>

            {/* STEP 1 — The business */}
            {step === 1 && (
              <div className="sponsor-form-grid">
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="label">Business name *</label>
                  <input className="input" placeholder="The Gardener's Arms" value={sponsorForm.business_name}
                    onChange={e => setSF({ business_name: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="label">Category</label>
                  <input className="input" placeholder="Food & Drink" value={sponsorForm.category}
                    onChange={e => setSF({ category: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="label">Website</label>
                  <input className="input" placeholder="https://…" value={sponsorForm.website}
                    onChange={e => setSF({ website: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="label">Contact name</label>
                  <input className="input" value={sponsorForm.contact_name}
                    onChange={e => setSF({ contact_name: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="label">Contact email</label>
                  <input className="input" type="email" value={sponsorForm.contact_email}
                    onChange={e => setSF({ contact_email: e.target.value })} />
                </div>
                <p style={{ gridColumn: '1 / -1', fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
                  The contact name &amp; email become the basis for the sponsor's login account when you set one up (you can still edit them at that point).
                </p>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="label">Logo</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ width: 112, height: 56, borderRadius: 12, background: 'rgba(196,148,58,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0, border: '1px solid var(--border)', padding: 4 }}>
                      {sponsorForm.logo_url
                        ? <img src={sponsorForm.logo_url} alt="Sponsor logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        : <span style={{ color: '#8A6220', fontWeight: 700, fontSize: 20 }}>{(sponsorForm.business_name || '?').charAt(0)}</span>}
                    </div>
                    <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer', margin: 0 }}>
                      <Upload size={14} /> {sponsorForm.logo_url ? 'Replace logo' : 'Upload logo'}
                      <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleSponsorLogoChange} />
                    </label>
                    {sponsorForm.logo_url && (
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setSF({ logo_url: '' }); setLogoTrimmed(false); }}>Remove</button>
                    )}
                  </div>
                  {logoTrimmed && (
                    <span style={{ fontSize: 12, color: 'var(--green)', marginTop: 6, display: 'block' }}>
                      We trimmed the empty space round the edge so the logo shows bigger.
                    </span>
                  )}
                  {!logoTrimmed && existingLogoTrim && (
                    <button type="button" className="btn btn-secondary btn-sm" style={{ marginTop: 8 }}
                      onClick={() => { setSF({ logo_url: existingLogoTrim }); setLogoTrimmed(true); }}>
                      Trim the empty edge so it shows bigger
                    </button>
                  )}
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>
                    Shown to members next to this sponsor's offers, and on the sponsor's own dashboard.
                  </span>
                </div>
              </div>
            )}

            {/* STEP 2 — The offer */}
            {step === 2 && (
              <div className="sponsor-form-grid">
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <div className="sponsor-label-row">
                    <label className="label" htmlFor="sponsor-offer">The offer</label>
                    {counter(offerLen, OFFER_MAX)}
                  </div>
                  <input id="sponsor-offer" className="input" placeholder="15% off food for members" value={sponsorForm.offer_title}
                    onChange={e => setSF({ offer_title: e.target.value })} />
                  <span className="sponsor-hint">
                    {offerLen > OFFER_MAX ? `Shorten this to ${OFFER_MAX} characters so it isn't cut off.` : 'What members get, in a few words. Shown on the offer card, at the till and in sponsor ads.'}
                  </span>
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <div className="sponsor-label-row">
                    <label className="label" htmlFor="sponsor-terms">Small print (optional)</label>
                    {counter(termsLen, TERMS_MAX)}
                  </div>
                  <textarea id="sponsor-terms" className="input" rows={2} placeholder="Mon–Thu, dine-in only, until 31 December"
                    value={sponsorForm.offer_description} onChange={e => setSF({ offer_description: e.target.value })} />
                  <span className="sponsor-hint">
                    {termsLen > TERMS_MAX ? `Shorten this to ${TERMS_MAX} characters so it isn't cut off.` : 'Days, exclusions and how long it lasts.'}
                  </span>
                </div>
                <div style={{ gridColumn: '1 / -1', fontSize: 13, fontWeight: 700, color: 'var(--charcoal)', marginTop: 4 }}>
                  What staff take off at the till
                </div>
                <div className="form-group">
                  <label className="label">Discount type</label>
                  <select className="input" value={sponsorForm.discount_type} onChange={e => setSF({ discount_type: e.target.value })}>
                    <option value="percentage">Percentage off</option>
                    <option value="fixed">Fixed amount off</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="label">{sponsorForm.discount_type === 'fixed' ? 'Amount off (£)' : 'Discount (%)'}</label>
                  <input className="input" type="number" min="0" step="0.01"
                    placeholder={sponsorForm.discount_type === 'fixed' ? 'e.g. 5' : 'e.g. 15'}
                    value={sponsorForm.discount_rate} onChange={e => setSF({ discount_rate: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="label">Minimum spend (£, optional)</label>
                  <input className="input" type="number" min="0" step="0.01" placeholder="e.g. 10"
                    value={sponsorForm.min_spend} onChange={e => setSF({ min_spend: e.target.value })} />
                </div>
                {sponsorForm.discount_type === 'percentage' && (
                  <div className="form-group">
                    <label className="label">Max discount cap (£, optional)</label>
                    <input className="input" type="number" min="0" step="0.01" placeholder="e.g. 20"
                      value={sponsorForm.max_discount_value} onChange={e => setSF({ max_discount_value: e.target.value })} />
                  </div>
                )}
                {mismatch && (
                  <div className="sponsor-mismatch" role="alert" style={{ gridColumn: '1 / -1' }}>
                    {mismatch.till
                      ? <>The offer says <strong>{mismatch.said}</strong>, but the till takes off <strong>{mismatch.till}</strong>. Members will expect what the offer says — make them match.</>
                      : <>The offer says <strong>{mismatch.said}</strong>, but no till discount is set yet. Enter it above so staff take off the right amount.</>}
                  </div>
                )}
                <p style={{ gridColumn: '1 / -1', fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
                  {badge
                    ? <>Members see the badge <strong>{badge}</strong> on the offer — made from the till discount, so they always match. </>
                    : null}
                  The actual sale value is captured at each redemption and compared to the sponsorship cost on the Attribution dashboard.
                </p>
              </div>
            )}

            {/* STEP 3 — Cost & dates */}
            {step === 3 && (
              <div className="sponsor-form-grid">
                <div className="form-group">
                  <label className="label">Tier</label>
                  <select className="input" value={sponsorForm.tier} onChange={e => setSF({ tier: e.target.value })}>
                    {['platinum', 'gold', 'silver', 'standard'].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="label">Sponsorship cost (£)</label>
                  <input className="input" type="number" min="0" step="0.01" placeholder="e.g. 500"
                    value={sponsorForm.sponsorship_cost} onChange={e => setSF({ sponsorship_cost: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="label">Start date</label>
                  <input className="input" type="date" value={sponsorForm.sponsorship_start}
                    onChange={e => setSF({ sponsorship_start: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="label">End date</label>
                  <input className="input" type="date" value={sponsorForm.sponsorship_end}
                    onChange={e => setSF({ sponsorship_end: e.target.value })} />
                </div>
                <label style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={sponsorForm.featured} onChange={e => setSF({ featured: e.target.checked })} />
                  {/* B-86: say what Featured now does. */}
                  Feature this sponsor (their logo and offer show in the bar at the bottom of the club hub for every member)
                </label>
                <p style={{ gridColumn: '1 / -1', fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
                  Cost powers cost-vs-revenue on the attribution dashboard. Dates are the agreement term; you choose when it actually goes live next.
                </p>
              </div>
            )}

            {/* STEP 4 — Go live (create only) */}
            {!isEdit && step === 4 && (
              <div>
                <div style={{ background: '#F4EFE6', borderRadius: 10, padding: '12px 14px', marginBottom: 16, fontSize: 13 }}>
                  <strong>{sponsorForm.business_name || 'New sponsor'}</strong>
                  {sponsorForm.offer_title ? ` — ${sponsorForm.offer_title}` : ''}
                  {sponsorForm.sponsorship_cost ? ` · £${sponsorForm.sponsorship_cost}` : ''}
                  {sponsorForm.sponsorship_start ? ` · ${sponsorForm.sponsorship_start}${sponsorForm.sponsorship_end ? '→' + sponsorForm.sponsorship_end : ''}` : ''}
                </div>
                {[
                  { key: 'start_now', icon: <Play size={16} />, title: 'Start now', desc: 'Goes live immediately — members can use the offer straight away.' },
                  { key: 'schedule', icon: <Calendar size={16} />, title: 'Schedule', desc: 'Goes live automatically on the start date you set.' },
                  { key: 'draft', icon: <Clock size={16} />, title: 'Save as draft', desc: "Save without going live — start it later from the list." },
                ].map(opt => (
                  <label key={opt.key} style={{
                    display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px', marginBottom: 8,
                    border: `2px solid ${sponsorWizard.goLive === opt.key ? '#1E4A35' : '#E8DFCE'}`, borderRadius: 10, cursor: 'pointer',
                    background: sponsorWizard.goLive === opt.key ? 'rgba(30,74,53,0.05)' : 'white'
                  }}>
                    <input type="radio" name="golive" checked={sponsorWizard.goLive === opt.key}
                      onChange={() => setSponsorWizard(p => ({ ...p, goLive: opt.key }))} style={{ marginTop: 3 }} />
                    <div>
                      <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>{opt.icon} {opt.title}</div>
                      <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{opt.desc}</div>
                    </div>
                  </label>
                ))}
                {scheduleNoDate && (
                  <p style={{ fontSize: 12.5, color: '#B33A2A', margin: '4px 0 0' }}>
                    Set a start date on the previous step to schedule, or choose Start now / Save as draft.
                  </p>
                )}
              </div>
            )}

            {/* Footer nav */}
            <div className="modal-actions" style={{ justifyContent: 'space-between' }}>
              <button className="btn btn-ghost" onClick={() => step === 1 ? closeSponsorWizard() : goStep(step - 1)}>
                {step === 1 ? 'Cancel' : <><ChevronLeft size={14} /> Back</>}
              </button>
              {step < totalSteps ? (
                <button className="btn btn-primary" disabled={!canNext} onClick={() => goStep(step + 1)}>
                  Next <ChevronRight size={14} />
                </button>
              ) : (
                <button className="btn btn-primary" disabled={saving || !sponsorForm.business_name.trim() || scheduleNoDate} onClick={submitSponsorWizard}>
                  {saving ? 'Saving…' : isEdit ? 'Save changes'
                    : sponsorWizard.goLive === 'start_now' ? 'Create & start'
                    : sponsorWizard.goLive === 'schedule' ? 'Create & schedule' : 'Save draft'}
                </button>
              )}
            </div>
          </div>
        </div>
        );
      })()}

      {/* ═══════════ EDIT TEAM MODAL ═══════════ */}
      {editingTeam && (
        <div className="modal-overlay" onClick={() => setEditingTeam(null)}>
          <div className="modal-card modal-md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Team</h3>
              <button className="btn btn-ghost btn-icon" onClick={() => setEditingTeam(null)}><X size={18} /></button>
            </div>
            <p className="modal-subtitle">{editingTeam.name}</p>
            <div className="sponsor-form-grid">
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="label">Team name *</label>
                <input className="input" value={editTeamForm.name}
                  onChange={e => setEditTeamForm(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="label">Age group</label>
                <input className="input" placeholder="e.g. Adult, U18, U15" value={editTeamForm.age_group}
                  onChange={e => setEditTeamForm(p => ({ ...p, age_group: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="label">Sport</label>
                <input className="input" placeholder="e.g. Rugby Union" value={editTeamForm.sport}
                  onChange={e => setEditTeamForm(p => ({ ...p, sport: e.target.value }))} />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="label">Description (optional)</label>
                <textarea className="input" rows={2} value={editTeamForm.description}
                  onChange={e => setEditTeamForm(p => ({ ...p, description: e.target.value }))} />
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-primary"
                style={{ background: accentColor }}
                disabled={!editTeamForm.name || updateTeamMutation.isPending}
                onClick={handleSaveEditTeam}
              >
                {updateTeamMutation.isPending ? 'Saving…' : 'Save team'}
              </button>
              <button className="btn btn-ghost" onClick={() => setEditingTeam(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {addingToTeam && isClubAdmin && (
        <TeamMembersPicker clubSlug={clubSlug} team={addingToTeam} accentColor={accentColor}
          onClose={() => setAddingToTeam(null)} />
      )}

      {/* ═══════════ SPONSOR LOGIN INVITE MODAL (UC-B3) ═══════════ */}
      {creatingAccountFor && (
        <div className="modal-overlay" onClick={closeSponsorInvite}>
          <div className="modal-card modal-md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Invite the sponsor to log in</h3>
              <button className="btn btn-ghost btn-icon" onClick={closeSponsorInvite} aria-label="Close"><X size={18} /></button>
            </div>
            {sponsorInvite ? (
              <div className="invite-form">
                <p className="modal-subtitle">
                  We've emailed <strong>{sponsorInvite.email}</strong> a link to set up the login for <strong>{creatingAccountFor.business_name}</strong>. They choose their own password; if they already have a Marakin account, they just sign in. The link lasts 7 days.
                </p>
                <p className="modal-subtitle" style={{ marginTop: 0 }}>You can also send them the link yourself:</p>
                <div className="modal-actions" style={{ flexWrap: 'wrap' }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => { navigator.clipboard.writeText(sponsorInvite.inviteUrl); setSponsorLinkCopied(true); }}>
                    <Copy size={14} /> {sponsorLinkCopied ? 'Copied!' : 'Copy link'}
                  </button>
                  <WhatsAppButton
                    text={inviteShareText(club?.name, sponsorInvite.inviteUrl, sponsorAccount.firstName)}
                    clubSlug={clubSlug} className="btn btn-ghost btn-sm"
                  />
                  <button className="btn btn-primary btn-sm" style={{ background: accentColor }} onClick={closeSponsorInvite}>Done</button>
                </div>
              </div>
            ) : (<>
            <p className="modal-subtitle">
              We'll email <strong>{creatingAccountFor.business_name}</strong> a link to set up their own login, so you never need to know their password. Pre-filled from the sponsor's contact details (edit if needed). They'll be able to scan codes and see their results.
            </p>
            {creatingAccountFor.linked_user_id && (
              <p className="sponsor-login-note">
                {/* WT-11 */}
                <strong>{creatingAccountFor.business_name} already has a login.</strong> If someone else accepts this new link, they become the sponsor's login instead. The old login then stops working for scanning and results; if that person has left, archive them in Members.
              </p>
            )}
            <div className="invite-form">
              <div className="sponsor-form-grid">
                <div className="form-group">
                  <label className="label">First name</label>
                  <input className="input" placeholder="Jane" value={sponsorAccount.firstName}
                    onChange={e => setSponsorAccount(p => ({ ...p, firstName: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="label">Last name</label>
                  <input className="input" placeholder="Smith" value={sponsorAccount.lastName}
                    onChange={e => setSponsorAccount(p => ({ ...p, lastName: e.target.value }))} />
                </div>
              </div>
              <div className="form-group">
                <label className="label">Email</label>
                <input className="input" type="email" placeholder="sponsor@example.com" value={sponsorAccount.email}
                  onChange={e => setSponsorAccount(p => ({ ...p, email: e.target.value }))} />
              </div>
              {inviteSponsorMutation.isError && (
                <p className="modal-note" role="alert" style={{ color: 'var(--danger)' }}>
                  {inviteSponsorMutation.error?.response?.data?.error || 'Something went wrong. Please try again.'}
                </p>
              )}
              <div className="modal-actions">
                <button className="btn btn-primary" style={{ background: accentColor }}
                  disabled={!sponsorAccount.email || inviteSponsorMutation.isPending}
                  onClick={() => inviteSponsorMutation.mutate({ sponsorId: creatingAccountFor.id, data: sponsorAccount })}>
                  {inviteSponsorMutation.isPending ? 'Sending…' : <><Mail size={14} /> Send invite</>}
                </button>
                <button className="btn btn-ghost" onClick={closeSponsorInvite}>Cancel</button>
              </div>
            </div>
            </>)}
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════ TAB HEAD — one header shape for every Club Admin screen (17 Sept) ═══════════ */
const TabHead = ({ icon: Icon, title, count, children }) => (
  <div className="admin-tab-head">
    <h2 className="admin-section-title">
      {Icon && <Icon size={18} />} {title}
      {count > 0 && <span className="tab-count">{count}</span>}
    </h2>
    {children && <div className="admin-tab-head-actions">{children}</div>}
  </div>
);

/* ═══════════ MEMBER ROW COMPONENT ═══════════ */
const MemberRow = ({ member, accentColor, isPending, onApprove, onDecline, onEdit, onToggleStatus, extra }) => {
  const m = member;
  const teamNames = m.teams?.filter(t => t.team_name).map(t => t.team_name) || [];
  const isActive = m.is_active !== false;
  const hasFoot = !!extra || (isPending && (!!onApprove || !!onDecline)) || !!onEdit || !!onToggleStatus;

  return (
    <div
      className={`member-row member-row-v2 card ${isPending ? 'member-pending' : ''}`}
      style={{
        opacity: !isActive ? 0.6 : 1,
        filter: !isActive ? 'grayscale(50%)' : 'none'
      }}
    >
      {/* One shape for every row (Stuart, 17 Sept): name + role on the first
          line, one muted meta line (team · email · joined), then pills and
          actions on a single footer line. No initials disc — it said nothing. */}
      <div className="member-main">
        <div className="member-info">
          <strong>{m.first_name} {m.last_name}</strong>
          <span className="member-meta">
            {teamNames.length > 0 && <span className="member-team">{teamNames.join(', ')}</span>}
            {m.email && <span className="member-email">{m.email}</span>}
            {m.joined_at && (
              <span className="member-joined" title={new Date(m.joined_at).toLocaleString()}>
                {new Date(m.joined_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
              </span>
            )}
          </span>
        </div>
        <span className={`badge ${ROLE_COLORS[m.role] || 'badge-silver'}`}>{ROLE_LABELS[m.role] || m.role}</span>
      </div>
      {/* WT-25: no footer at all when there's nothing to put in it (child rows,
          and a team manager's read-only rows) — it was an empty strip. */}
      {hasFoot && (
      <div className="member-foot">
        {extra && <div className="member-extra">{extra}</div>}
      <div className="member-actions">
        {isPending && onApprove && (
          <button className="btn btn-primary btn-sm" onClick={onApprove}>
            <CheckCircle size={14} /> Approve
          </button>
        )}
        {isPending && onDecline && (
          <button className="btn btn-ghost btn-sm" title="They'll see a neutral message and can't ask again for 30 days" onClick={onDecline}>
            <XCircle size={14} /> Decline
          </button>
        )}
        {onEdit && (
          <button className="btn btn-ghost btn-sm" title="Edit role, team and jersey number — or remove from the club" onClick={onEdit}>
            <Edit3 size={14} /> Edit
          </button>
        )}
        {onToggleStatus && (
          <button
            className="btn btn-ghost btn-sm"
            title={isActive ? 'Archive member' : 'Reactivate member'}
            onClick={onToggleStatus}
            style={{
              color: isActive ? '#666' : '#1E4A35'
            }}
          >
            <Archive size={14} />
            {isActive ? 'Archive' : 'Reactivate'}
          </button>
        )}
      </div>
      </div>
      )}
    </div>
  );
};

export default AdminPage;
